import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Select from 'react-select';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, FileText, CheckCircle2, Clock, AlertCircle, XCircle, Download, Send, Edit, Trash2, Target, Plus, Upload, MessageSquare, RotateCcw, Save, ChevronLeft, ChevronRight, Users, CreditCard } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/components.css';
import { createFormFieldHandler, createNestedFormFieldHandler, createSelectHandler, createToggleHandler, createMonthNavigationHandler, createWeekSelectionHandler } from '../utils/eventHandlers';

const TaskTracker = () => {
  const { user } = useAuth();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('submitted');
  const [loading, setLoading] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);

  const [dropdowns, setDropdowns] = useState({
    quarters: [],
    tasks: [],
    subtasks: [],
    trainingModes: [],
    trainings: [],
    statuses: [],
    states: [],
    districts: [],
    buildingProjects: [],
    buildingStatuses: [],
    typologies: [],
  });

  const [submittedTasks, setSubmittedTasks] = useState([]);
  const [savedTasks, setSavedTasks] = useState([]);
  const [existingBuildings, setExistingBuildings] = useState([]);
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);
  const [paymentStatusEntries, setPaymentStatusEntries] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    quarterId: '',
    paymentStatusId: '',
    remarks: '',
    paymentReceivedDate: '',
  });
  const [paymentFormErrors, setPaymentFormErrors] = useState({});
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [showWeekPicker, setShowWeekPicker] = useState(false);
  const [weekPickerMonth, setWeekPickerMonth] = useState(() => {
    // Always start from current date (ISO week numbering)
    return new Date();
  });
  const weekPickerRef = useRef(null);

  const [form, setForm] = useState({
    quarterId: '',
    taskDate: '',
    taskCompletionDate: '',
    taskId: '',
    subtaskId: '',
    trainingId: '',
    training: {
      startDate: '',
      district: '',
      venue: '',
      totalAttendees: '',
      totalWomen: '',
      totalMen: '',
      govtAttendees: '',
      privateAttendees: '',
      professionalAttendees: '',
      architectsCount: '',
      studentAttendees: '',
      otherAttendees: '',
      modeId: '',
    },
    buildingProjectId: '',
    buildingStatusId: '',
    buildingDetails: {
      name: '',
      address: '',
      stateId: '',
      districtId: '',
      latitude: '',
      longitude: '',
      nodalContactName: '',
      nodalDesignation: '',
      nodalEmail: '',
      nodalContactNumber: '',
      statusId: '',
      typology: '',
      epiValueBase: '',
      epiValueProposed: '',
      epiRatio: '', // Calculated in backend: epiValueProposed / epiValueBase
    },
    buildingLabellingProjectId: '',
    buildingLabellingStatusId: '',
    buildingLabellingDetails: {
      name: '',
      address: '',
      stateId: '',
      districtId: '',
      latitude: '',
      longitude: '',
      nodalContactName: '',
      nodalDesignation: '',
      nodalEmail: '',
      nodalContactNumber: '',
      statusId: '',
    },
    buildingTask5ProjectId: '',
    buildingTask5Details: {
      name: '',
      address: '',
      stateId: '',
      districtId: '',
      latitude: '',
      longitude: '',
      nodalContactName: '',
      nodalDesignation: '',
      nodalEmail: '',
      nodalContactNumber: '',
      statusId: '',
    },
    buildingTask5Subtask1Details: {
      name: '',
      address: '',
      buildingConstructionStatus: '',
      stateId: '',
      districtId: '',
      latitude: '',
      longitude: ''
    },
    remarks: '',
    statusId: '',
      stakeholderName: '', // Stakeholder name when status is "Awaiting Stakeholder's response"
      taskCompletionDate: '',
      isNotified: '', // For Task 1, Subtask 2b: 'yes' or 'no'
      notificationDate: '', // For Task 1, Subtask 2b: Date of notification
      incorporationInByLaws: '', // For Task 1, Subtask 2b: 'yes' or 'no'
    documents: [], // Array of File objects for new uploads
    documentPaths: [], // Array of existing document paths (for editing)
    buildingAction: 'new', // 'new' or 'update'
    existingBuildingId: null,
    newStatusId: '',
    newDistrictId: '',
    newLatitude: '',
    newLongitude: '',
    updateBuildingName: '',
    updateBuildingAddress: '',
    buildingTask5Subtask2Details: { 
      name: '', 
      address: '', 
      districtId: '', 
      complianceLevel: '', 
      typology: '', 
      ulbName: '', 
      epiBase: '', 
      epiProposed: '',
      epiRatio: '',
      latitude: '',
      longitude: '',
      statusId: ''
    },
    buildingTask5Subtask2Action: 'new',
    existingBuildingTask5Subtask2Id: null,
    updateBuildingTask5Subtask2Name: '',
    updateBuildingTask5Subtask2Address: '',
    updateBuildingTask5Subtask2DistrictId: '',
    updateBuildingTask5Subtask2ComplianceLevel: '',
    updateBuildingTask5Subtask2Typology: '',
    updateBuildingTask5Subtask2UlbName: '',
    updateBuildingTask5Subtask2EpiBase: '',
    updateBuildingTask5Subtask2EpiProposed: '',
    updateBuildingTask5Subtask2EpiRatio: '',
    updateBuildingTask5Subtask2Latitude: '',
    updateBuildingTask5Subtask2Longitude: '',
    updateBuildingTask5Subtask2StatusId: '',
  });

  const loadDropdowns = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/masters/dropdowns');
      setDropdowns(data);
    } catch (e) {
      toast.error('Failed to load dropdown data');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmitted = async () => {
    try {
      const { data } = await axios.get('/api/tasks/submitted');
      setSubmittedTasks(data);
    } catch (e) {
      console.error('Error loading submitted tasks:', e);
      if (e.response?.data?.details) {
        console.error('Backend error details:', e.response.data.details);
      }
    }
  };

  const loadSaved = async () => {
    try {
      const { data } = await axios.get('/api/tasks/saved');
      setSavedTasks(data);
    } catch (e) {
      console.error('Error loading saved tasks:', e);
      if (e.response?.data?.details) {
        console.error('Backend error details:', e.response.data.details);
      }
    }
  };

  const loadExistingBuildings = async () => {
    try {
      const { data } = await axios.get('/api/buildings/user');
      setExistingBuildings(data || []);
    } catch (e) {
      console.error('Error loading existing buildings:', e);
      if (e.response?.data?.details) {
        console.error('Backend error details:', e.response.data.details);
      }
      setExistingBuildings([]);
    }
  };

  const loadPaymentStatusOptions = async () => {
    try {
      const { data } = await axios.get('/api/masters/paymentstatusmaster');
      setPaymentStatusOptions(data || []);
    } catch (e) {
      console.error('Error loading payment status options:', e);
      setPaymentStatusOptions([]);
    }
  };

  const loadPaymentStatusEntries = async () => {
    try {
      const { data } = await axios.get('/api/tasks/payment-status');
      setPaymentStatusEntries(data || []);
    } catch (e) {
      console.error('Error loading payment status entries:', e);
      setPaymentStatusEntries([]);
    }
  };

  const selectedPaymentStatusOption = useMemo(() => {
    if (!paymentForm.paymentStatusId || !paymentStatusOptions.length) return null;
    return paymentStatusOptions.find((o) => String(o.id) === String(paymentForm.paymentStatusId));
  }, [paymentForm.paymentStatusId, paymentStatusOptions]);

  const paymentStatusRequiresDate = useMemo(() => {
    if (!selectedPaymentStatusOption) return false;
    const id = Number(selectedPaymentStatusOption.id);
    const label = (selectedPaymentStatusOption.status ?? selectedPaymentStatusOption.payment_status ?? selectedPaymentStatusOption.name ?? '').toString().toLowerCase();
    return id === 3 || label === 'released';
  }, [selectedPaymentStatusOption]);

  // Quarters that already have "Released" (id 3) – disable them in dropdown
  const releasedQuarterIds = useMemo(() => {
    return new Set(
      paymentStatusEntries
        .filter((e) => Number(e.payment_status_id) === 3)
        .map((e) => String(e.quarter_id))
    );
  }, [paymentStatusEntries]);

  // For selected quarter: max status id already saved (flow is 1 -> 2 -> 3; don't allow going back)
  const currentMaxPaymentStatusIdForSelectedQuarter = useMemo(() => {
    if (!paymentForm.quarterId) return 0;
    const entriesForQuarter = paymentStatusEntries.filter(
      (e) => String(e.quarter_id) === String(paymentForm.quarterId)
    );
    if (entriesForQuarter.length === 0) return 0;
    return Math.max(...entriesForQuarter.map((e) => Number(e.payment_status_id)), 0);
  }, [paymentForm.quarterId, paymentStatusEntries]);

  const handlePaymentFormSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!paymentForm.quarterId) err.quarterId = 'Quarter is required';
    if (!paymentForm.paymentStatusId) err.paymentStatusId = 'Payment Status is required';
    if (paymentStatusRequiresDate && !paymentForm.paymentReceivedDate) err.paymentReceivedDate = 'Payment Received Date is required when status is Released';
    setPaymentFormErrors(err);
    if (Object.keys(err).length > 0) {
      toast.error('Please fill all required fields.');
      return;
    }
    setLoading(true);
    const payload = {
      quarterId: paymentForm.quarterId,
      paymentStatusId: paymentForm.paymentStatusId,
      remarks: paymentForm.remarks || undefined,
      paymentReceivedDate: paymentStatusRequiresDate ? paymentForm.paymentReceivedDate : undefined,
    };
    try {
      if (editingPaymentId) {
        await axios.put(`/api/tasks/payment-status/${editingPaymentId}`, payload);
        toast.success('Payment status updated successfully.');
      } else {
        await axios.post('/api/tasks/payment-status', payload);
        toast.success('Payment status saved successfully.');
      }
      setPaymentForm({ quarterId: '', paymentStatusId: '', remarks: '', paymentReceivedDate: '' });
      setPaymentFormErrors({});
      setEditingPaymentId(null);
      loadPaymentStatusEntries();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save payment status.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentEdit = (entry) => {
    setPaymentForm({
      quarterId: String(entry.quarter_id),
      paymentStatusId: String(entry.payment_status_id),
      remarks: entry.remarks || '',
      paymentReceivedDate: entry.payment_received_date ? entry.payment_received_date.split('T')[0] : '',
    });
    setEditingPaymentId(entry.id);
    setPaymentFormErrors({});
  };

  const handlePaymentDelete = async (entry) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    setLoading(true);
    try {
      await axios.delete(`/api/tasks/payment-status/${entry.id}`);
      toast.success('Entry deleted.');
      if (editingPaymentId === entry.id) {
        setEditingPaymentId(null);
        setPaymentForm({ quarterId: '', paymentStatusId: '', remarks: '', paymentReceivedDate: '' });
      }
      loadPaymentStatusEntries();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmitEntry = async (entry) => {
    if (!window.confirm('Are you sure you want to submit this to BEE?')) return;
    setLoading(true);
    try {
      await axios.post(`/api/tasks/payment-status/${entry.id}/submit`);
      toast.success('Payment status submitted successfully.');
      loadPaymentStatusEntries();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to submit.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    loadSubmitted();
    loadSaved();
    loadExistingBuildings();
    loadPaymentStatusOptions();
    loadPaymentStatusEntries();
  }, []);

  // Week picker month is now always based on current date (ISO week numbering)
  // No need to update based on establishment date

  // Close week picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (weekPickerRef.current && !weekPickerRef.current.contains(event.target)) {
        setShowWeekPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'saved' || tab === 'submitted' || tab === 'add' || tab === 'payment') {
      setActiveTab(tab);
    }
  }, [location.search]);

  const selectedTask = useMemo(() => dropdowns.tasks.find((t) => String(t.task_id) === String(form.taskId)), [dropdowns.tasks, form.taskId]);
  const selectedSubtask = useMemo(() => dropdowns.subtasks.find((s) => String(s.subtask_id) === String(form.subtaskId)), [dropdowns.subtasks, form.subtaskId]);

  // Get selected quarter's quarter_no
  const selectedQuarterNo = useMemo(() => {
    if (!form.quarterId || !dropdowns.quarters) return null;
    const quarter = dropdowns.quarters.find((q) => String(q.id) === String(form.quarterId));
    return quarter ? Number(quarter.quarter_no) : null;
  }, [form.quarterId, dropdowns.quarters]);

  // Check if Task 4 should be disabled (from Quarter 4 onwards)
  const isTask4Disabled = useMemo(() => {
    return selectedQuarterNo !== null && selectedQuarterNo >= 4;
  }, [selectedQuarterNo]);

  // Clear Task 4 if it's selected when switching to Quarter 4+
  useEffect(() => {
    if (isTask4Disabled && form.taskId) {
      const task = dropdowns.tasks.find((t) => String(t.task_id) === String(form.taskId));
      if (task && Number(task.task_no) === 4) {
        setForm(prev => ({ ...prev, taskId: '', subtaskId: '' }));
        toast.error('Task 4 is not available from Quarter 4 onwards');
      }
    }
  }, [isTask4Disabled, form.taskId, dropdowns.tasks]);

  // Conditional visibility per requirements
  const showTrainingDetails = useMemo(() => {
    if (!selectedTask || !selectedSubtask) {
      // Fallback: check form values directly if selectedTask/selectedSubtask are not available
      if (form.taskId && form.subtaskId) {
        const task = dropdowns.tasks.find((t) => String(t.task_id) === String(form.taskId));
        const subtask = dropdowns.subtasks.find((s) => String(s.subtask_id) === String(form.subtaskId));
        if (task && subtask) {
          const taskNo = Number(task.task_no);
          const subtaskNo = Number(subtask.subtask_no);
          return (taskNo === 3 && (subtaskNo === 2 || subtaskNo === 3 || subtaskNo === 4 || subtaskNo === 5));
        }
      }
      return false;
    }
    // Open for Task 3, Subtasks 2, 3, 4, and 5
    const taskNo = Number(selectedTask.task_no);
    const subtaskNo = Number(selectedSubtask.subtask_no);
    return (taskNo === 3 && (subtaskNo === 2 || subtaskNo === 3 || subtaskNo === 4 || subtaskNo === 5));
  }, [selectedTask, selectedSubtask, form.taskId, form.subtaskId, dropdowns.tasks, dropdowns.subtasks]);

  // Check if Task 1, Subtask 2b is selected (for notification fields)
  const showNotificationFields = useMemo(() => {
    if (!selectedTask || !selectedSubtask) {
      // Fallback: check form values directly if selectedTask/selectedSubtask are not available
      if (form.taskId && form.subtaskId) {
        const task = dropdowns.tasks.find((t) => String(t.task_id) === String(form.taskId));
        const subtask = dropdowns.subtasks.find((s) => String(s.subtask_id) === String(form.subtaskId));
        if (task && subtask) {
          const taskNo = Number(task.task_no);
          const subtaskNo = String(subtask.subtask_no || '').toLowerCase();
          return (taskNo === 1 && subtaskNo === '2b');
        }
      }
      return false;
    }
    // Check for Task 1, Subtask 2b
    const taskNo = Number(selectedTask.task_no);
    const subtaskNo = String(selectedSubtask.subtask_no || '').toLowerCase();
    return (taskNo === 1 && subtaskNo === '2b');
  }, [selectedTask, selectedSubtask, form.taskId, form.subtaskId, dropdowns.tasks, dropdowns.subtasks]);

  // Filter trainings based on task/subtask
  const filteredTrainings = useMemo(() => {
    const allTrainings = dropdowns.trainings || [];
    
    if (!selectedTask || !selectedSubtask) {
      // Fallback: check form values directly
      if (form.taskId && form.subtaskId) {
        const task = dropdowns.tasks.find((t) => String(t.task_id) === String(form.taskId));
        const subtask = dropdowns.subtasks.find((s) => String(s.subtask_id) === String(form.subtaskId));
        if (task && subtask) {
          const taskNo = Number(task.task_no);
          const subtaskNo = Number(subtask.subtask_no);
          if (taskNo === 3 && subtaskNo === 2) {
            // Task 3, Subtask 2: Show only Awareness Program trainings
            return allTrainings.filter(t => {
              const trainingType = (t.training_type || '').toLowerCase();
              return trainingType.includes('awareness') || trainingType.includes('awareness program');
            });
          } else if (taskNo === 3 && subtaskNo === 3) {
            // Task 3, Subtask 3: Show only Training-A trainings
            return allTrainings.filter(t => {
              const trainingType = (t.training_type || '').toLowerCase();
              return trainingType.includes('training-a') || trainingType.includes('training a');
            });
          } else if (taskNo === 3 && subtaskNo === 4) {
            // Task 3, Subtask 4: Show only Training-B trainings
            return allTrainings.filter(t => {
              const trainingType = (t.training_type || '').toLowerCase();
              return trainingType.includes('training-b') || trainingType.includes('training b');
            });
          } else if (taskNo === 3 && subtaskNo === 5) {
            // Task 3, Subtask 5: Show only Training-C trainings
            return allTrainings.filter(t => {
              const trainingType = (t.training_type || '').toLowerCase();
              return trainingType.includes('training-c') || trainingType.includes('training c');
            });
          }
        }
      }
      return allTrainings;
    }
    const taskNo = Number(selectedTask.task_no);
    const subtaskNo = Number(selectedSubtask.subtask_no);
    if (taskNo === 3 && subtaskNo === 2) {
      // Task 3, Subtask 2: Show only Awareness Program trainings
      return allTrainings.filter(t => {
        const trainingType = (t.training_type || '').toLowerCase();
        return trainingType.includes('awareness') || trainingType.includes('awareness program');
      });
    } else if (taskNo === 3 && subtaskNo === 3) {
      // Task 3, Subtask 3: Show only Training-A trainings
      return allTrainings.filter(t => {
        const trainingType = (t.training_type || '').toLowerCase();
        return trainingType.includes('training-a') || trainingType.includes('training a');
      });
    } else if (taskNo === 3 && subtaskNo === 4) {
      // Task 3, Subtask 4: Show only Training-B trainings
      return allTrainings.filter(t => {
        const trainingType = (t.training_type || '').toLowerCase();
        return trainingType.includes('training-b') || trainingType.includes('training b');
      });
    } else if (taskNo === 3 && subtaskNo === 5) {
      // Task 3, Subtask 5: Show only Training-C trainings
      return allTrainings.filter(t => {
        const trainingType = (t.training_type || '').toLowerCase();
        return trainingType.includes('training-c') || trainingType.includes('training c');
      });
    }
    return allTrainings;
  }, [selectedTask, selectedSubtask, form.taskId, form.subtaskId, dropdowns.tasks, dropdowns.subtasks, dropdowns.trainings]);

  // Determine if district is required based on training mode (for Task 3, Subtasks 2, 3, 4)
  const isDistrictRequired = useMemo(() => {
    // Only apply this logic for Task 3, Subtasks 2, 3, and 4
    const taskNo = selectedTask ? Number(selectedTask.task_no) : (form.taskId ? Number(dropdowns.tasks.find(t => String(t.task_id) === String(form.taskId))?.task_no) : null);
    const subtaskNo = selectedSubtask ? Number(selectedSubtask.subtask_no) : (form.subtaskId ? Number(dropdowns.subtasks.find(s => String(s.subtask_id) === String(form.subtaskId))?.subtask_no) : null);
    
    if (taskNo === 3 && (subtaskNo === 2 || subtaskNo === 3 || subtaskNo === 4)) {
      // Get selected training's mode
      if (form.trainingId && dropdowns.trainings) {
        const selectedTraining = dropdowns.trainings.find(t => String(t.training_id) === String(form.trainingId));
        if (selectedTraining) {
          const trainingModeId = selectedTraining.training_mode_id || selectedTraining.Training_Mode_ID;
          // training_mode_id 1 = Online (not required), 2 = Physical (required)
          return trainingModeId === 2;
        }
      }
      // If no training selected yet, default to required (will update when training is selected)
      return true;
    }
    // For all other tasks/subtasks, district is required
    return true;
  }, [selectedTask, selectedSubtask, form.taskId, form.subtaskId, form.trainingId, dropdowns.tasks, dropdowns.subtasks, dropdowns.trainings]);

  // Filter typologies based on task and subtask
  const filteredTypologies = useMemo(() => {
    const allTypologies = dropdowns.typologies || [];
    
    if (!selectedTask || !selectedSubtask) {
      // Fallback: check form values directly
      if (form.taskId && form.subtaskId) {
        const task = dropdowns.tasks.find((t) => String(t.task_id) === String(form.taskId));
        const subtask = dropdowns.subtasks.find((s) => String(s.subtask_id) === String(form.subtaskId));
        if (task && subtask) {
          const taskNo = Number(task.task_no);
          const subtaskNo = Number(subtask.subtask_no);
          
          // Task 2, Subtask 2: Exclude residential typologies
          if (taskNo === 2 && subtaskNo === 2) {
            return allTypologies.filter(t => {
              const isResidential = t.isResidential || t.is_residential || false;
              return !isResidential;
            });
          }
          
          // Task 2, Subtask 3: Only show residential typologies
          if (taskNo === 2 && subtaskNo === 3) {
            return allTypologies.filter(t => {
              const isResidential = t.isResidential || t.is_residential || false;
              return isResidential;
            });
          }
        }
      }
      return allTypologies;
    }
    
    const taskNo = Number(selectedTask.task_no);
    const subtaskNo = Number(selectedSubtask.subtask_no);
    
    // Task 2, Subtask 2: Exclude residential typologies
    if (taskNo === 2 && subtaskNo === 2) {
      return allTypologies.filter(t => {
        const isResidential = t.isResidential || t.is_residential || false;
        return !isResidential;
      });
    }
    
    // Task 2, Subtask 3: Only show residential typologies
    if (taskNo === 2 && subtaskNo === 3) {
      return allTypologies.filter(t => {
        const isResidential = t.isResidential || t.is_residential || false;
        return isResidential;
      });
    }
    
    // For all other tasks/subtasks, show all typologies
    return allTypologies;
  }, [selectedTask, selectedSubtask, form.taskId, form.subtaskId, dropdowns.tasks, dropdowns.subtasks, dropdowns.typologies]);

  const showBuildingDetailsLabellingFalse = useMemo(() => {
    if (!selectedTask || !selectedSubtask) {
      // Fallback: check form values directly if selectedTask/selectedSubtask are not available
      if (form.taskId && form.subtaskId) {
        const task = dropdowns.tasks.find((t) => String(t.task_id) === String(form.taskId));
        const subtask = dropdowns.subtasks.find((s) => String(s.subtask_id) === String(form.subtaskId));
        if (task && subtask) {
          const taskNo = Number(task.task_no);
          const subtaskNo = Number(subtask.subtask_no);
          // Task 2, Subtasks 2 and 3: Show buildings where islabelling=false AND isother=false OR projectbuildingid=1 OR projectbuildingid=2
          // Task 4, All subtasks: Show building form for all subtasks
          return (taskNo === 2 && (subtaskNo === 2 || subtaskNo === 3)) || taskNo === 4;
        }
      }
      return false;
    }
    // Task 2, Subtasks 2 and 3: Show buildings where islabelling=false AND isother=false OR projectbuildingid=1 OR projectbuildingid=2
    // Task 4, All subtasks: Show building form for all subtasks
    const taskNo = Number(selectedTask.task_no);
    const subtaskNo = Number(selectedSubtask.subtask_no);
    return (taskNo === 2 && (subtaskNo === 2 || subtaskNo === 3)) || taskNo === 4;
  }, [selectedTask, selectedSubtask, form.taskId, form.subtaskId, dropdowns.tasks, dropdowns.subtasks]);

  // Get user's cell state_id for Task 2, Subtasks 2 and 3
  const userCellStateId = useMemo(() => {
    if (!user?.cellState || !dropdowns.states) return null;
    const state = dropdowns.states.find(s => s.state_ut === user.cellState);
    return state ? state.state_id : null;
  }, [user?.cellState, dropdowns.states]);

  // Auto-set state when building form is shown (Task 2, Task 4, etc.)
  useEffect(() => {
    if (showBuildingDetailsLabellingFalse && userCellStateId && form.buildingAction === 'new') {
      if (!form.buildingDetails.stateId || form.buildingDetails.stateId !== String(userCellStateId)) {
        setForm(prev => ({
          ...prev,
          buildingDetails: {
            ...prev.buildingDetails,
            stateId: String(userCellStateId),
            districtId: '' // Reset district when state changes
          }
        }));
      }
    }
  }, [showBuildingDetailsLabellingFalse, userCellStateId, form.buildingAction]);

  const showBuildingDetailsLabellingTrue = useMemo(() => {
    if (!selectedTask || !selectedSubtask) return false;
    // Task 4, Subtask 1, isLabelling true
    return (selectedTask.task_no === 4 && selectedSubtask.subtask_no === 1);
  }, [selectedTask, selectedSubtask]);

  const showBuildingDetailsTask5 = useMemo(() => {
    if (!selectedTask || !selectedSubtask) return false;
    // Task 5, Subtask 2, with isOther filters
    return (selectedTask.task_no === 5 && selectedSubtask.subtask_no === 2);
  }, [selectedTask, selectedSubtask]);

  const showBuildingDetailsTask5Subtask1 = useMemo(() => {
    if (!selectedTask || !selectedSubtask) return false;
    // Task 5, Subtask 1, with isOther_ECSBC filters
    return (Number(selectedTask.task_no) === 5 && Number(selectedSubtask.subtask_no) === 1);
  }, [selectedTask, selectedSubtask]);

  const showBuildingDetailsTask5Subtask2 = useMemo(() => {
    if (!selectedTask || !selectedSubtask) return false;
    // Task 5, Subtask 2, with compliance_level
    return (Number(selectedTask.task_no) === 5 && Number(selectedSubtask.subtask_no) === 2);
  }, [selectedTask, selectedSubtask]);

  const filteredBuildingProjectsFalse = useMemo(() => {
    return (dropdowns.buildingProjects || []).filter((p) => p.islabelling === false && p.isother === false);
  }, [dropdowns.buildingProjects]);

  // Building statuses for "Building Project Details (Non-Labelling)" section:
  // - Task 2: show only statuses where isother === true (from buildingprojectstatusmaster)
  // - Task 4: show only statuses where islabelling === true (from buildingprojectstatusmaster)
  const filteredBuildingStatusesFalse = useMemo(() => {
    const isTask4 = selectedTask?.task_no === 4 || (form.taskId && dropdowns.tasks?.find((t) => String(t.task_id) === String(form.taskId))?.task_no === 4);
    const isTask2 = selectedTask?.task_no === 2 || (form.taskId && dropdowns.tasks?.find((t) => String(t.task_id) === String(form.taskId))?.task_no === 2);
    
    let filtered;
    if (isTask4) {
      // Task 4: show building statuses where islabelling is true
      filtered = (dropdowns.buildingStatuses || []).filter((s) => s.islabelling === true);
    } else if (isTask2) {
      // Task 2: show building statuses where isother is true only
      filtered = (dropdowns.buildingStatuses || []).filter((s) => s.isother === true);
    } else {
      filtered = [];
    }
    
    // Remove duplicates by building_status name, keeping the first occurrence
    const seen = new Set();
    return filtered.filter((s) => {
      if (seen.has(s.building_status)) {
        return false;
      }
      seen.add(s.building_status);
      return true;
    });
  }, [dropdowns.buildingStatuses, selectedTask, form.taskId, dropdowns.tasks]);

  const filteredBuildingProjectsTrue = useMemo(() => {
    return (dropdowns.buildingProjects || []).filter((p) => p.islabelling === true);
  }, [dropdowns.buildingProjects]);

  const filteredBuildingStatusesTrue = useMemo(() => {
    const filtered = (dropdowns.buildingStatuses || []).filter((s) => s.islabelling === true);
    // Remove duplicates by building_status name, keeping the first occurrence
    const seen = new Set();
    return filtered.filter((s) => {
      if (seen.has(s.building_status)) {
        return false;
      }
      seen.add(s.building_status);
      return true;
    });
  }, [dropdowns.buildingStatuses]);

  const filteredBuildingProjectsTask5 = useMemo(() => {
    const allProjects = dropdowns.buildingProjects || [];
    return allProjects.filter((p) => p.isother === true);
  }, [dropdowns.buildingProjects]);

  const filteredBuildingStatusesTask5 = useMemo(() => {
    const filtered = (dropdowns.buildingStatuses || []).filter((s) => s.isother === true);
    // Remove duplicates by building_status name, keeping the first occurrence
    const seen = new Set();
    return filtered.filter((s) => {
      if (seen.has(s.building_status)) {
        return false;
      }
      seen.add(s.building_status);
      return true;
    });
  }, [dropdowns.buildingStatuses]);

  const filteredBuildingProjectsTask5Subtask1 = useMemo(() => {
    const allProjects = dropdowns.buildingProjects || [];
    return allProjects.filter((p) => p.isother === true);
  }, [dropdowns.buildingProjects]);

  const filteredBuildingStatusesTask5Subtask1 = useMemo(() => {
    const filtered = (dropdowns.buildingStatuses || []).filter((s) => s.isother_ecsbc === true);
    // Remove duplicates by building_status name, keeping the first occurrence
    const seen = new Set();
    return filtered.filter((s) => {
      if (seen.has(s.building_status)) {
        return false;
      }
      seen.add(s.building_status);
      return true;
    });
  }, [dropdowns.buildingStatuses]);

  // Helper function to sort subtasks properly (handles numeric and alphanumeric like "1", "2a", "2b", "10", "11")
  const sortSubtasks = (subtasks) => {
    return [...subtasks].sort((a, b) => {
      const aNo = String(a.subtask_no || '');
      const bNo = String(b.subtask_no || '');
      
      // Extract numeric part and letter part (if any)
      const aMatch = aNo.match(/^(\d+)([a-z]?)$/i);
      const bMatch = bNo.match(/^(\d+)([a-z]?)$/i);
      
      if (!aMatch || !bMatch) {
        // Fallback to string comparison if format doesn't match
        return aNo.localeCompare(bNo);
      }
      
      const aNum = parseInt(aMatch[1], 10);
      const bNum = parseInt(bMatch[1], 10);
      const aLetter = aMatch[2] || '';
      const bLetter = bMatch[2] || '';
      
      // First compare by numeric part
      if (aNum !== bNum) {
        return aNum - bNum;
      }
      
      // If numeric parts are equal, compare by letter part
      return aLetter.localeCompare(bLetter);
    });
  };

  const subtasksForTask = useMemo(() => {
    if (!form.taskId) return [];
    const filtered = (dropdowns.subtasks || []).filter((s) => String(s.task_id) === String(form.taskId));
    return sortSubtasks(filtered);
  }, [dropdowns.subtasks, form.taskId]);

  // Check if buildings exist for current task/subtask
  const buildingsForCurrentTask = useMemo(() => {
    if (existingBuildings.length === 0) return [];
    if (!selectedTask || !selectedSubtask) return [];
    
    // Filter buildings based on the same task_id AND subtask_id
    // Match by subtask_id from SubtaskMaster to ensure exact subtask matching
    const currentTaskId = form.taskId ? String(form.taskId) : (selectedTask.task_id ? String(selectedTask.task_id) : null);
    const currentSubtaskId = form.subtaskId ? String(form.subtaskId) : (selectedSubtask.subtask_id ? String(selectedSubtask.subtask_id) : null);
    const currentTaskNo = Number(selectedTask.task_no);
    const currentSubtaskNo = Number(selectedSubtask.subtask_no);
    
    let filteredBuildings = existingBuildings.filter(building => {
      const buildingTaskId = building.task_id ? String(building.task_id) : null;
      const buildingSubtaskId = building.subtask_id ? String(building.subtask_id) : null;
      const buildingTaskNo = building.task_no ? Number(building.task_no) : null;
      const buildingSubtaskNo = building.subtask_no ? Number(building.subtask_no) : null;
      
      // Match by both task_id and subtask_id (most accurate - matches SubtaskMaster)
      if (currentTaskId && currentSubtaskId && buildingTaskId && buildingSubtaskId) {
        return buildingTaskId === currentTaskId && buildingSubtaskId === currentSubtaskId;
      }
      
      // Fallback: Match by task_no and subtask_no
      if (buildingTaskNo && buildingSubtaskNo) {
        return buildingTaskNo === currentTaskNo && buildingSubtaskNo === currentSubtaskNo;
      }
      
      return false;
    });
    
    // Remove duplicates based on building_id
    const uniqueBuildings = [];
    const seenBuildingIds = new Set();
    filteredBuildings.forEach(building => {
      if (!seenBuildingIds.has(building.building_id)) {
        seenBuildingIds.add(building.building_id);
        uniqueBuildings.push(building);
      }
    });
    
    // Return all fields from the building object, don't filter them out
    // Return ALL fields from the building object - don't filter them out
    return uniqueBuildings.map(building => ({
      ...building, // Spread all fields from the original building object first
      // Then override with formatted values for display
      status: building.status || 'Current Status',
      project_type: building.project_type || 'Project Type',
      state: building.state_ut || building.state || 'State',
      district: building.district_name || building.district || 'District'
    }));
  }, [existingBuildings, selectedTask, selectedSubtask, form.taskId, form.subtaskId]);

  const onFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/zip',
        'application/x-zip-compressed'
      ];
      
      const files = Array.from(e.target.files);
      const validFiles = [];
      const errors = [];
      
      files.forEach((file, index) => {
        // Check file size
      if (file.size > maxSize) {
          errors.push(`${file.name}: File size exceeds 5MB limit`);
        return;
      }
      
        // Check file type
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const isValidType = allowedTypes.includes(file.type) || 
          ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'zip'].includes(fileExtension);
        
        if (!isValidType) {
          errors.push(`${file.name}: File type not supported`);
          return;
        }
        
        validFiles.push(file);
      });
      
      if (errors.length > 0) {
        errors.forEach(error => toast.error(error));
      }
      
      if (validFiles.length > 0) {
        // Only allow a single file - replace any existing file
        setForm((prev) => ({ 
          ...prev, 
          documents: [validFiles[0]] // Only take the first file
        }));
        toast.success('File selected successfully');
    }
      
      // Clear the input to allow selecting the same files again
      e.target.value = '';
    }
  };
  
  const removeDocument = (index) => {
    setForm((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
    toast.success('File removed');
  };
  
  const removeExistingDocument = (index) => {
    setForm((prev) => ({
      ...prev,
      documentPaths: prev.documentPaths.filter((_, i) => i !== index)
    }));
    toast.success('File removed');
  };

  // Task colors (matching other pages)
  const getTaskColor = (taskNo) => {
    const taskColors = {
      1: '#f97316', // Orange (distinct)
      2: '#8b5cf6', // Purple
      3: '#ec4899', // Pink
      4: '#06b6d4', // Cyan
      5: '#3b82f6', // Blue
      6: '#14b8a6', // Teal
      7: '#f43f5e', // Rose
      8: '#a855f7', // Violet
    };
    return taskColors[taskNo] || '#f97316'; // Default orange
  };

  // Status colors
  const getStatusColor = (status) => {
    if (!status) return 'text-gray-700 bg-gray-100 border border-gray-200';
    const statusLower = status.toLowerCase().trim().replace(/-/g, ' ');
    
    if (statusLower.includes('completed')) {
      return 'text-green-700 bg-green-50 border border-green-200'; // Green
    } else if (statusLower.includes('in progress') || statusLower.includes('in-progress')) {
      return 'text-purple-700 bg-purple-50 border border-purple-200'; // Purple
    } else if (statusLower.includes('on hold') || statusLower.includes('on-hold')) {
      return 'text-orange-700 bg-orange-50 border border-orange-200'; // Orange
    } else if (statusLower.includes('not started') || statusLower.includes('not-started')) {
      return 'text-gray-700 bg-gray-100 border border-gray-200'; // Gray
    } else if (statusLower.includes('awaiting') || statusLower.includes('pending')) {
      return 'text-blue-700 bg-blue-50 border border-blue-200'; // Blue for awaiting/pending
    } else if (statusLower.includes('submitted')) {
      return 'text-indigo-700 bg-indigo-50 border border-indigo-200'; // Indigo for submitted
    }
    return 'text-gray-700 bg-gray-100 border border-gray-200';
  };

  // Helper function to strip HTML tags while preserving line breaks
  const stripHtmlTagsPreserveLines = (html) => {
    if (!html) return '';
    // Create a temporary div element
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Get text content (this preserves line breaks from block elements)
    let text = tmp.textContent || tmp.innerText || '';
    // Clean up: replace multiple spaces with single space, but preserve newlines
    text = text.replace(/[ \t]+/g, ' '); // Replace multiple spaces/tabs with single space
    text = text.replace(/\n\s*\n/g, '\n'); // Replace multiple newlines with single newline
    return text.trim();
  };

  // Safe React component to render remarks with highlighted stakeholder name
  const RemarksWithStakeholder = ({ remarks }) => {
    if (!remarks) return null;
    
    // Strip HTML tags from remarks
    const cleanRemarks = stripHtmlTagsPreserveLines(remarks);
    
    // Check if remarks contain stakeholder name prefix
    if (cleanRemarks.includes('STAKEHOLDER_NAME:')) {
      // Split by newlines to handle the format
      const lines = cleanRemarks.split('\n');
      const firstLine = lines[0];
      
      if (firstLine.startsWith('STAKEHOLDER_NAME:')) {
        const stakeholderName = firstLine.replace('STAKEHOLDER_NAME:', '').trim();
        const remainingRemarks = lines.slice(1).join('\n').replace(/^\n+/, '');
        
        return (
          <>
            <div className="stakeholder-badge-container">
              <span className="stakeholder-badge">
                <span className="stakeholder-label">Stakeholder:</span>
                <span className="stakeholder-name">{stakeholderName}</span>
          </span>
            </div>
            {remainingRemarks && (
              <div className="whitespace-pre-wrap">{remainingRemarks}</div>
            )}
          </>
        );
      }
    }
    
    // If no stakeholder name, return remarks as is (without HTML tags)
    return <div className="whitespace-pre-wrap">{cleanRemarks}</div>;
  };

  // Helper function to get week from date (using cell-specific week numbering)
  // Helper function to format date range for week display
  const formatWeekDateRange = (monday, sunday) => {
    const mondayMonth = monday.toLocaleDateString('en-US', { month: 'short' });
    const mondayDay = monday.getDate();
    const mondayYear = monday.getFullYear();
    
    const sundayMonth = sunday.toLocaleDateString('en-US', { month: 'short' });
    const sundayDay = sunday.getDate();
    const sundayYear = sunday.getFullYear();
    
    // If same month and year, show: "Dec 1-7, 2025"
    if (mondayMonth === sundayMonth && mondayYear === sundayYear) {
      return `${mondayMonth} ${mondayDay}-${sundayDay}, ${mondayYear}`;
    }
    // If different months but same year, show: "Dec 1 - Jan 7, 2025"
    if (mondayYear === sundayYear) {
      return `${mondayMonth} ${mondayDay} - ${sundayMonth} ${sundayDay}, ${mondayYear}`;
    }
    // If different years, show: "Dec 1, 2025 - Jan 7, 2026"
    return `${mondayMonth} ${mondayDay}, ${mondayYear} - ${sundayMonth} ${sundayDay}, ${sundayYear}`;
  };

  const getWeekFromDate = (dateInput) => {
    if (!dateInput) return 'Select Week';
    
    let date;
    
    // Handle Date object
    if (dateInput instanceof Date) {
      date = new Date(dateInput);
    }
    // Handle week input format (YYYY-W##) - treat as ISO week
    else if (typeof dateInput === 'string' && dateInput.includes('W')) {
      const [year, week] = dateInput.split('-W');
      const yearNum = parseInt(year);
      const weekNum = parseInt(week);
      
      // Calculate ISO week 1 Monday (week containing January 4th)
          const jan4 = new Date(yearNum, 0, 4);
      const jan4DayOfWeek = jan4.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      // If Jan 4 is Sunday (0), Monday is Jan 5. Otherwise, go back (jan4DayOfWeek - 1) days
      const jan4DaysToMonday = jan4DayOfWeek === 0 ? 1 : -(jan4DayOfWeek - 1);
      const week1Monday = new Date(yearNum, 0, 4 + jan4DaysToMonday);
        week1Monday.setHours(0, 0, 0, 0);
      
      // Calculate the Monday of the requested week
      const targetMonday = new Date(week1Monday);
      targetMonday.setDate(week1Monday.getDate() + (weekNum - 1) * 7);
      targetMonday.setHours(0, 0, 0, 0);
      
      // Get Sunday of the week
      const targetSunday = new Date(targetMonday);
      targetSunday.setDate(targetMonday.getDate() + 6);
      targetSunday.setHours(0, 0, 0, 0);
      
      return formatWeekDateRange(targetMonday, targetSunday);
    }
    // Handle date string (YYYY-MM-DD or ISO format)
    else if (typeof dateInput === 'string') {
      // Extract date part from ISO string if present (e.g., "2026-12-28T00:00:00.000Z" -> "2026-12-28")
      const dateStr = dateInput.split('T')[0];
      const dateParts = dateStr.split('-');
      if (dateParts.length !== 3) return 'Select Week';
      
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
      const day = parseInt(dateParts[2]);
      date = new Date(year, month, day);
    } else {
      return 'Select Week';
    }
    
    if (isNaN(date.getTime())) return 'Select Week';
    
    // Normalize to local date (avoid timezone issues)
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const localDate = new Date(year, month, day);
    
    // Get the date's day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfWeek = localDate.getDay();
    
    // Get Monday of the week
    // If Sunday (0), Monday is next day. Otherwise, go back (dayOfWeek - 1) days
    const daysToMonday = dayOfWeek === 0 ? 1 : -(dayOfWeek - 1);
    const monday = new Date(localDate);
    monday.setDate(localDate.getDate() + daysToMonday);
    monday.setHours(0, 0, 0, 0); // Normalize to midnight
    
    // Get Sunday of the week
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(0, 0, 0, 0); // Normalize to midnight
    
    // Return formatted date range
    return formatWeekDateRange(monday, sunday);
  };

  // Helper function to calculate ISO week number for edit (fallback)
  const calculateISOWeekNumberForEdit = (monday, year) => {
    // ISO week 1 is the week containing January 4th
    const jan4 = new Date(year, 0, 4);
    const jan4DayOfWeek = jan4.getDay();
    const jan4DaysToMonday = jan4DayOfWeek === 0 ? 1 : -(jan4DayOfWeek - 1);
    const week1Monday = new Date(year, 0, 4 + jan4DaysToMonday);
    const daysDiff = Math.floor((monday - week1Monday) / (24 * 60 * 60 * 1000));
    let weekNumber = Math.floor(daysDiff / 7) + 1;
    if (weekNumber < 1) weekNumber = 1;
    if (weekNumber > 52) weekNumber = 52;
    return weekNumber;
  };

  // Helper function to calculate ISO week number (fallback)
  const calculateISOWeekNumber = (monday, year) => {
    // ISO week 1 is the week containing January 4th
    const jan4 = new Date(year, 0, 4);
    const jan4DayOfWeek = jan4.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // If Jan 4 is Sunday (0), Monday is Jan 5. Otherwise, go back (jan4DayOfWeek - 1) days
    const jan4DaysToMonday = jan4DayOfWeek === 0 ? 1 : -(jan4DayOfWeek - 1);
    const week1Monday = new Date(year, 0, 4 + jan4DaysToMonday);
    
    // Calculate week number
    const daysDiff = Math.floor((monday - week1Monday) / (24 * 60 * 60 * 1000));
    let weekNumber = Math.floor(daysDiff / 7) + 1;
    
    // Handle edge case: if the date is in early January but belongs to previous year's last week
    if (weekNumber < 1) {
      const prevYear = year - 1;
      const prevJan4 = new Date(prevYear, 0, 4);
      const prevJan4DayOfWeek = prevJan4.getDay();
      const prevDaysToMonday = prevJan4DayOfWeek === 0 ? 1 : -(prevJan4DayOfWeek - 1);
      const prevWeek1Monday = new Date(prevYear, 0, 4 + prevDaysToMonday);
      const prevDaysDiff = Math.floor((monday - prevWeek1Monday) / (24 * 60 * 60 * 1000));
      weekNumber = Math.floor(prevDaysDiff / 7) + 1;
    }
    
    // Handle edge case: if the date is in late December but belongs to next year's first week
    if (weekNumber > 52) {
      const nextYear = year + 1;
      const nextJan4 = new Date(nextYear, 0, 4);
      const nextJan4DayOfWeek = nextJan4.getDay();
      const nextDaysToMonday = nextJan4DayOfWeek === 0 ? 1 : -(nextJan4DayOfWeek - 1);
      const nextWeek1Monday = new Date(nextYear, 0, 4 + nextDaysToMonday);
      if (monday >= nextWeek1Monday) {
        weekNumber = 1;
      }
    }
    
    return weekNumber;
  };

  // Helper function to get ISO week number for a given date
  const getCellWeekNumber = (date) => {
    // Get Monday of the week containing the given date
    const dateDayOfWeek = date.getDay();
    const dateDaysToMonday = dateDayOfWeek === 0 ? 1 : -(dateDayOfWeek - 1);
    const dateMonday = new Date(date);
    dateMonday.setDate(date.getDate() + dateDaysToMonday);
    dateMonday.setHours(0, 0, 0, 0);
    
    const year = dateMonday.getFullYear();
    
    // Use ISO week numbering
    return calculateISOWeekNumber(dateMonday, year);
  };

  // Helper function to get all weeks for a given month with ISO week numbers
  const getWeeksForMonth = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    
    // Get first day of month
    const firstDay = new Date(year, month, 1);
    // Get last day of month
    const lastDay = new Date(year, month + 1, 0);
    
    // Get Monday of the week containing first day
    const firstDayOfWeek = firstDay.getDay();
    const daysToFirstMonday = firstDayOfWeek === 0 ? 1 : -(firstDayOfWeek - 1);
    const startMonday = new Date(year, month, 1 + daysToFirstMonday);
    
    // Get Monday of the week containing last day
    const lastDayOfWeek = lastDay.getDay();
    const daysToLastMonday = lastDayOfWeek === 0 ? 1 : -(lastDayOfWeek - 1);
    const endMonday = new Date(year, month, lastDay.getDate() + daysToLastMonday);
    
    const weeks = [];
    let currentMonday = new Date(startMonday);
    
    while (currentMonday <= endMonday) {
        const weekEnd = new Date(currentMonday);
        weekEnd.setDate(currentMonday.getDate() + 6);
        
      const isoWeekNumber = getCellWeekNumber(currentMonday);
        
      if (isoWeekNumber !== null) {
          weeks.push({
            monday: new Date(currentMonday),
            sunday: weekEnd,
          cellWeekNumber: isoWeekNumber,
            isCurrentMonth: currentMonday.getMonth() === month
          });
        }
      
      currentMonday.setDate(currentMonday.getDate() + 7);
    }
    
    return weeks;
  };

  // Helper function to convert ISO week format to calendar date
  const convertCellWeekToDate = (weekFormat) => {
    if (!weekFormat || !weekFormat.includes('W')) {
      return weekFormat; // Return as-is if not week format
    }
    
    const [year, week] = weekFormat.split('-W');
    const yearNum = parseInt(year);
    const weekNum = parseInt(week);
    
    // Calculate ISO week 1 Monday (week containing January 4th)
    const jan4 = new Date(yearNum, 0, 4);
    const jan4DayOfWeek = jan4.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // If Jan 4 is Sunday (0), Monday is Jan 5. Otherwise, go back (jan4DayOfWeek - 1) days
    const jan4DaysToMonday = jan4DayOfWeek === 0 ? 1 : -(jan4DayOfWeek - 1);
    const week1Monday = new Date(yearNum, 0, 4 + jan4DaysToMonday);
        week1Monday.setHours(0, 0, 0, 0);
    
    // Calculate the Monday of the requested week
    const targetMonday = new Date(week1Monday);
    targetMonday.setDate(week1Monday.getDate() + (weekNum - 1) * 7);
    targetMonday.setHours(0, 0, 0, 0); // Normalize to midnight
    
    // Format as YYYY-MM-DD
    const yearStr = targetMonday.getFullYear();
    const monthStr = String(targetMonday.getMonth() + 1).padStart(2, '0');
    const dayStr = String(targetMonday.getDate()).padStart(2, '0');
    return `${yearStr}-${monthStr}-${dayStr}`;
  };

  const saveTask = async (submitAfterCreate) => {
    // Validate required fields
    if (!form.quarterId) {
      toast.error('Please select a Quarter');
      return;
    }
    if (!form.taskDate) {
      toast.error('Please select a Task Week');
      return;
    }
    if (!form.taskId) {
      toast.error('Please select a Task');
      return;
    }
    if (!form.statusId) {
      toast.error('Please select a Status');
      return;
    }

    // Validate subtask - always required
    if (!form.subtaskId) {
      toast.error('Please select a Subtask');
      return;
    }

    // Check if status is "Completed"
    const selectedStatus = dropdowns.statuses.find(s => String(s.status_id) === String(form.statusId));
    const isCompleted = selectedStatus && selectedStatus.status && selectedStatus.status.toLowerCase().trim() === 'completed';

    // Validate task completion date - required only when status is "Completed"
    if (isCompleted && !form.taskCompletionDate) {
      toast.error('Please select Task Completion Date (required when status is Completed)');
      return;
    }

    // Validate documents - required only when status is "Completed"
    if (isCompleted && form.documents.length === 0 && form.documentPaths.length === 0) {
      toast.error('Please upload a Document (required when status is Completed)');
      return;
    }

    // Validate remarks - always required
    if (!form.remarks || form.remarks.trim() === '') {
      toast.error('Please enter Remarks');
      return;
    }

    // Validate notification fields for Task 1, Subtask 2b
    if (showNotificationFields) {
      if (!form.isNotified) {
        toast.error('Please select whether the notification has been made');
        setLoading(false);
        return;
      }
      if (form.isNotified === 'yes' && !form.notificationDate) {
        toast.error('Please select the date of notification');
        setLoading(false);
        return;
      }
      if (!form.incorporationInByLaws) {
        toast.error('Please select whether incorporation in Building By-Laws has been done');
        setLoading(false);
        return;
      }
    }

    // Validate building details if Task 2, Subtasks 2 or 3 is selected
    if (showBuildingDetailsLabellingFalse && form.buildingAction === 'new') {
      // Check if using buildings array or single building form
      if (form.buildings && form.buildings.length > 0) {
        // Validate each building in the array has required fields
        for (let i = 0; i < form.buildings.length; i++) {
          const building = form.buildings[i];
          if (!building.name || !building.address || !building.districtId || !building.latitude || !building.longitude || !building.nodalContactName || !building.nodalDesignation || !building.nodalEmail || !building.statusId) {
            toast.error(`Building ${i + 1} is missing required fields (Name, Address, District, Latitude, Longitude, Nodal Contact Person Name, Department, Email ID, or Status)`);
            return;
          }
        }
      } else if (!form.buildings || form.buildings.length === 0) {
        // If no buildings array, check single building form
        if (!form.buildingDetails.name || !form.buildingDetails.address || !form.buildingDetails.districtId || !form.buildingDetails.latitude || !form.buildingDetails.longitude || !form.buildingDetails.nodalContactName || !form.buildingDetails.nodalDesignation || !form.buildingDetails.nodalEmail || !form.buildingDetails.statusId || !form.buildingDetails.typology) {
          toast.error('Please fill all required building fields (Name, Address, District, Latitude, Longitude, Nodal Contact Person Name, Department, Email ID, Building Status, and Typology)');
          return;
        }
      }
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('quarterId', String(form.quarterId));
      // Convert cell-specific week format to calendar date before sending
      const taskDateToSend = convertCellWeekToDate(form.taskDate);
      fd.append('taskDate', String(taskDateToSend));
      fd.append('taskId', String(form.taskId));
      if (form.subtaskId) fd.append('subtaskId', String(form.subtaskId));

      // Add notification fields for Task 1, Subtask 2b
      if (showNotificationFields) {
        if (form.isNotified) fd.append('isNotified', String(form.isNotified));
        if (form.isNotified === 'yes' && form.notificationDate) {
          fd.append('notificationDate', String(form.notificationDate));
        }
        if (form.incorporationInByLaws) fd.append('incorporationInByLaws', String(form.incorporationInByLaws));
      }

      // Validate and append training details for Task 3 (Subtasks 2–5)
      if (showTrainingDetails) {
        // Training selection itself is required whenever the Training Details block is visible
        if (!form.trainingId) {
          toast.error('Please select a Training');
          setLoading(false);
          return;
        }

        // Validate all required training fields are filled
        const isDistrictMissing = isDistrictRequired && !form.training.district;
        if (!form.training.startDate || isDistrictMissing ||
            !form.training.totalAttendees || !form.training.totalWomen || !form.training.totalMen ||
            !form.training.govtAttendees || !form.training.privateAttendees || 
            !form.training.professionalAttendees || !form.training.architectsCount ||
            !form.training.studentAttendees || !form.training.otherAttendees) {
          toast.error('Please fill all required training fields');
          setLoading(false);
          return;
        }
        
        // Validate that each participant group count is less than or equal to total attendees
        const totalAttendees = Number(form.training.totalAttendees || 0);
        const totalWomen = Number(form.training.totalWomen || 0);
        const totalMen = Number(form.training.totalMen || 0);
        const govtAttendees = Number(form.training.govtAttendees || 0);
        const privateAttendees = Number(form.training.privateAttendees || 0);
        const professionalAttendees = Number(form.training.professionalAttendees || 0);
        const architectsCount = Number(form.training.architectsCount || 0);
        const studentAttendees = Number(form.training.studentAttendees || 0);
        const otherAttendees = Number(form.training.otherAttendees || 0);

        // Basic sanity check
        if (totalAttendees <= 0) {
          toast.error('Total No. of Attendees must be greater than 0');
          setLoading(false);
          return;
        }
        
        const checks = [
          { label: 'Total No. of Female Participants', value: totalWomen },
          { label: 'Total No. of Male Participants', value: totalMen },
          { label: 'Total No. of Government Sector Participants', value: govtAttendees },
          { label: 'Total No. of Private Sector Participants', value: privateAttendees },
          { label: 'Total No. of Professionals Participants', value: professionalAttendees },
          { label: 'Total No. of Architects Participants', value: architectsCount },
          { label: 'Total No. of Students Participants', value: studentAttendees },
          { label: 'Total No. of Other Participants', value: otherAttendees },
        ];
        
        for (const check of checks) {
          if (check.value < 0) {
            toast.error(`${check.label} cannot be negative`);
          setLoading(false);
          return;
        }
          if (check.value > totalAttendees) {
            toast.error(`${check.label} (${check.value}) cannot be greater than Total No. of Attendees (${totalAttendees})`);
            setLoading(false);
            return;
          }
        }
        
        fd.append('trainingId', String(form.trainingId));
        
        // Check if selected training is Training-A (1-day), Training-B (2-day), or Training-C (2-day)
        const selectedTraining = dropdowns.trainings.find(t => String(t.training_id) === String(form.trainingId));
        const trainingType = selectedTraining?.training_type?.toLowerCase() || '';
        const isTrainingA = trainingType.includes('training-a') || trainingType.includes('training a');
        const isTrainingB = trainingType.includes('training-b') || trainingType.includes('training b');
        const isTrainingC = trainingType.includes('training-c') || trainingType.includes('training c');
        
        // Calculate dates based on training type
        const trainingStartDate = form.training.startDate || null;
        let trainingEndDate = null;
        
        if (isTrainingA) {
          // Training-A: 1-day duration, start date = end date
          trainingEndDate = trainingStartDate;
        } else if ((isTrainingB || isTrainingC) && trainingStartDate) {
          // Training-B and Training-C: 2-day duration, end date = start date + 1 day
          const startDate = new Date(trainingStartDate);
          startDate.setDate(startDate.getDate() + 1);
          // Format as YYYY-MM-DD
          const year = startDate.getFullYear();
          const month = String(startDate.getMonth() + 1).padStart(2, '0');
          const day = String(startDate.getDate()).padStart(2, '0');
          trainingEndDate = `${year}-${month}-${day}`;
        } else if (trainingStartDate) {
          // For other trainings, end date = start date + 1 day
          const startDate = new Date(trainingStartDate);
          startDate.setDate(startDate.getDate() + 1);
          // Format as YYYY-MM-DD
          const year = startDate.getFullYear();
          const month = String(startDate.getMonth() + 1).padStart(2, '0');
          const day = String(startDate.getDate()).padStart(2, '0');
          trainingEndDate = `${year}-${month}-${day}`;
        }
        
        const trainingPayload = {
          startDate: trainingStartDate,
          endDate: trainingEndDate,
          district: form.training.district || null,
          venue: form.training.venue || null,
          totalAttendees: totalAttendees,
          totalWomen: Number(form.training.totalWomen || 0),
          totalMen: Number(form.training.totalMen || 0),
          govtAttendees: Number(form.training.govtAttendees || 0),
          privateAttendees: Number(form.training.privateAttendees || 0),
          professionalAttendees: Number(form.training.professionalAttendees || 0),
          architectsCount: Number(form.training.architectsCount || 0),
          studentAttendees: Number(form.training.studentAttendees || 0),
          otherAttendees: Number(form.training.otherAttendees || 0),
        };
        fd.append('trainingDetails', JSON.stringify(trainingPayload));
      }

      if (showBuildingDetailsLabellingFalse) {
        // If editing and existingBuildingId is set, update the building
        if (editingTaskId && form.existingBuildingId) {
          fd.append('buildingAction', 'update');
          fd.append('existingBuildingId', String(form.existingBuildingId));
          if (form.buildingProjectId) {
            fd.append('buildingProjectId', String(form.buildingProjectId));
          }
          const bd = {
            name: form.buildingDetails.name,
            address: form.buildingDetails.address,
            stateId: userCellStateId || form.buildingDetails.stateId, // Always use cell state ID if available
            districtId: form.buildingDetails.districtId,
            latitude: form.buildingDetails.latitude,
            longitude: form.buildingDetails.longitude,
            nodalContactName: form.buildingDetails.nodalContactName,
            nodalDesignation: form.buildingDetails.nodalDesignation,
            nodalEmail: form.buildingDetails.nodalEmail,
            nodalContactNumber: form.buildingDetails.nodalContactNumber,
            statusId: form.buildingDetails.statusId,
            typology: form.buildingDetails.typology,
            epiValueBase: form.buildingDetails.epiValueBase || null,
            epiValueProposed: form.buildingDetails.epiValueProposed || null,
          };
          fd.append('buildingDetails', JSON.stringify(bd));
        } else if (form.buildingAction === 'update' && form.existingBuildingId) {
          // Handle building status update only
          fd.append('buildingAction', 'update');
          fd.append('existingBuildingId', String(form.existingBuildingId));
          fd.append('newStatusId', String(form.buildingDetails.statusId));
        } else if (form.buildingAction === 'new') {
          // Handle new building creation
          fd.append('buildingAction', 'new');
          const bd = {
            name: form.buildingDetails.name,
            address: form.buildingDetails.address,
            stateId: userCellStateId || form.buildingDetails.stateId, // Always use cell state ID if available
            districtId: form.buildingDetails.districtId,
            latitude: form.buildingDetails.latitude,
            longitude: form.buildingDetails.longitude,
            nodalContactName: form.buildingDetails.nodalContactName,
            nodalDesignation: form.buildingDetails.nodalDesignation,
            nodalEmail: form.buildingDetails.nodalEmail,
            nodalContactNumber: form.buildingDetails.nodalContactNumber,
            statusId: form.buildingDetails.statusId,
            typology: form.buildingDetails.typology,
            epiValueBase: form.buildingDetails.epiValueBase || null,
            epiValueProposed: form.buildingDetails.epiValueProposed || null,
          };
          fd.append('buildingDetails', JSON.stringify(bd));
        }
      }

      if (showBuildingDetailsLabellingTrue && form.buildingLabellingProjectId) {
        // Validate building labelling details
        if (!form.buildingLabellingDetails.name || !form.buildingLabellingDetails.address || !form.buildingLabellingDetails.districtId || !form.buildingLabellingDetails.latitude || !form.buildingLabellingDetails.longitude || !form.buildingLabellingDetails.nodalContactName || !form.buildingLabellingDetails.nodalEmail || !form.buildingLabellingDetails.statusId) {
          toast.error('Please fill all required building labelling fields (Name, Address, District, Latitude, Longitude, Nodal Contact Person Name, Email ID, and Status)');
          setLoading(false);
          return;
        }
        fd.append('buildingLabellingProjectId', String(form.buildingLabellingProjectId));
        const bdl = {
          name: form.buildingLabellingDetails.name,
          address: form.buildingLabellingDetails.address,
          stateId: userCellStateId || form.buildingLabellingDetails.stateId, // Always use cell state ID if available
          districtId: form.buildingLabellingDetails.districtId,
          latitude: form.buildingLabellingDetails.latitude,
          longitude: form.buildingLabellingDetails.longitude,
          nodalContactName: form.buildingLabellingDetails.nodalContactName,
          nodalDesignation: form.buildingLabellingDetails.nodalDesignation,
          nodalEmail: form.buildingLabellingDetails.nodalEmail,
          nodalContactNumber: form.buildingLabellingDetails.nodalContactNumber,
          statusId: form.buildingLabellingDetails.statusId,
        };
        fd.append('buildingLabellingDetails', JSON.stringify(bdl));
      }

      if (showBuildingDetailsTask5 && form.buildingTask5ProjectId) {
        // Validate building task 5 details
        if (!form.buildingTask5Details.name || !form.buildingTask5Details.address || !form.buildingTask5Details.districtId || !form.buildingTask5Details.latitude || !form.buildingTask5Details.longitude || !form.buildingTask5Details.nodalContactName || !form.buildingTask5Details.nodalEmail || !form.buildingTask5Details.statusId) {
          toast.error('Please fill all required building task 5 fields (Name, Address, District, Latitude, Longitude, Nodal Contact Person Name, Email ID, and Status)');
          setLoading(false);
          return;
        }
        fd.append('buildingTask5ProjectId', String(form.buildingTask5ProjectId));
        const bd5 = {
          name: form.buildingTask5Details.name,
          address: form.buildingTask5Details.address,
          stateId: userCellStateId || form.buildingTask5Details.stateId, // Always use cell state ID if available
          districtId: form.buildingTask5Details.districtId,
          latitude: form.buildingTask5Details.latitude,
          longitude: form.buildingTask5Details.longitude,
          nodalContactName: form.buildingTask5Details.nodalContactName,
          nodalDesignation: form.buildingTask5Details.nodalDesignation,
          nodalEmail: form.buildingTask5Details.nodalEmail,
          nodalContactNumber: form.buildingTask5Details.nodalContactNumber,
          statusId: form.buildingTask5Details.statusId,
        };
        fd.append('buildingTask5Details', JSON.stringify(bd5));
      }

      if (showBuildingDetailsTask5Subtask1 && form.buildingAction === 'new') {
        // Validate building task 5 subtask 1 details - only Name, Address, and Building Construction Status required
        const trimmedName = form.buildingTask5Subtask1Details.name?.trim() || '';
        const trimmedAddress = form.buildingTask5Subtask1Details.address?.trim() || '';
        if (!trimmedName || !trimmedAddress || !form.buildingTask5Subtask1Details.buildingConstructionStatus) {
          toast.error('Please fill all required building fields (Name, Address, and Building Construction Status). Fields cannot be empty or whitespace only.');
          setLoading(false);
          return;
        }
        fd.append('buildingAction', 'new');
        const bd5s1 = {
          name: trimmedName,
          address: trimmedAddress,
          buildingConstructionStatus: form.buildingTask5Subtask1Details.buildingConstructionStatus,
          districtId: form.buildingTask5Subtask1Details.districtId || null,
          latitude: form.buildingTask5Subtask1Details.latitude || null,
          longitude: form.buildingTask5Subtask1Details.longitude || null
        };
        fd.append('buildingTask5Subtask1Details', JSON.stringify(bd5s1));
      } else if (showBuildingDetailsTask5Subtask1 && form.buildingAction === 'update') {
        // Validate update action
        const trimmedName = form.updateBuildingName?.trim() || '';
        const trimmedAddress = form.updateBuildingAddress?.trim() || '';
        if (!form.existingBuildingId || !trimmedName || !trimmedAddress || !form.newStatusId) {
          toast.error('Please fill all required building fields (Name, Address, and Building Construction Status). Fields cannot be empty or whitespace only.');
          setLoading(false);
          return;
        }
        fd.append('buildingAction', 'update');
        fd.append('existingBuildingId', form.existingBuildingId);
        fd.append('updateBuildingName', trimmedName);
        fd.append('updateBuildingAddress', trimmedAddress);
        fd.append('newStatusId', form.newStatusId);
        fd.append('newDistrictId', form.newDistrictId || null);
        fd.append('newLatitude', form.newLatitude || null);
        fd.append('newLongitude', form.newLongitude || null);
      } else if (showBuildingDetailsTask5Subtask2 && form.buildingTask5Subtask2Action === 'new') {
        // Validate building task 5 subtask 2 details
        const trimmedName = form.buildingTask5Subtask2Details.name?.trim() || '';
        const trimmedAddress = form.buildingTask5Subtask2Details.address?.trim() || '';
        const trimmedComplianceLevel = form.buildingTask5Subtask2Details.complianceLevel?.trim() || '';
        const trimmedTypology = form.buildingTask5Subtask2Details.typology?.trim() || '';
        const trimmedUlbName = form.buildingTask5Subtask2Details.ulbName?.trim() || '';
        
        if (!trimmedName || !trimmedAddress || !form.buildingTask5Subtask2Details.districtId || !trimmedComplianceLevel || !trimmedTypology || !trimmedUlbName) {
          toast.error('Please fill all required building fields (Name, Address, District, Compliance Level, Typology, and ULB Name). Fields cannot be empty or whitespace only.');
          setLoading(false);
          return;
        }
        fd.append('buildingTask5Subtask2Action', 'new');
        const bd5s2 = {
          name: trimmedName,
          address: trimmedAddress,
          districtId: form.buildingTask5Subtask2Details.districtId,
          complianceLevel: trimmedComplianceLevel,
          typology: trimmedTypology,
          ulbName: trimmedUlbName,
          epiBase: form.buildingTask5Subtask2Details.epiBase || null,
          epiProposed: form.buildingTask5Subtask2Details.epiProposed || null,
          latitude: form.buildingTask5Subtask2Details.latitude || null,
          longitude: form.buildingTask5Subtask2Details.longitude || null,
          statusId: form.buildingTask5Subtask2Details.statusId || null,
        };
        fd.append('buildingTask5Subtask2Details', JSON.stringify(bd5s2));
      } else if (showBuildingDetailsTask5Subtask2 && form.buildingTask5Subtask2Action === 'update') {
        // Validate update action for Task 5 Subtask 2
        const trimmedName = form.updateBuildingTask5Subtask2Name?.trim() || '';
        const trimmedAddress = form.updateBuildingTask5Subtask2Address?.trim() || '';
        const trimmedComplianceLevel = form.updateBuildingTask5Subtask2ComplianceLevel?.trim() || '';
        const trimmedTypology = form.updateBuildingTask5Subtask2Typology?.trim() || '';
        const trimmedUlbName = form.updateBuildingTask5Subtask2UlbName?.trim() || '';
        
        if (!form.existingBuildingTask5Subtask2Id || !trimmedName || !trimmedAddress || !form.updateBuildingTask5Subtask2DistrictId || !trimmedComplianceLevel || !trimmedTypology || !trimmedUlbName) {
          toast.error('Please fill all required building fields (Name, Address, District, Compliance Level, Typology, and ULB Name). Fields cannot be empty or whitespace only.');
          setLoading(false);
          return;
        }
        fd.append('buildingTask5Subtask2Action', 'update');
        fd.append('existingBuildingTask5Subtask2Id', form.existingBuildingTask5Subtask2Id);
        fd.append('updateBuildingTask5Subtask2Name', trimmedName);
        fd.append('updateBuildingTask5Subtask2Address', trimmedAddress);
        fd.append('updateBuildingTask5Subtask2DistrictId', form.updateBuildingTask5Subtask2DistrictId);
        fd.append('updateBuildingTask5Subtask2ComplianceLevel', trimmedComplianceLevel);
        fd.append('updateBuildingTask5Subtask2Typology', trimmedTypology);
        fd.append('updateBuildingTask5Subtask2UlbName', trimmedUlbName);
        fd.append('updateBuildingTask5Subtask2EpiBase', form.updateBuildingTask5Subtask2EpiBase || null);
        fd.append('updateBuildingTask5Subtask2EpiProposed', form.updateBuildingTask5Subtask2EpiProposed || null);
        fd.append('updateBuildingTask5Subtask2Latitude', form.updateBuildingTask5Subtask2Latitude || null);
        fd.append('updateBuildingTask5Subtask2Longitude', form.updateBuildingTask5Subtask2Longitude || null);
        fd.append('updateBuildingTask5Subtask2StatusId', form.updateBuildingTask5Subtask2StatusId || null);
      }

      // Append all documents
      form.documents.forEach((doc, index) => {
        fd.append(`documents`, doc);
      });
      
      // Append existing document paths (for editing)
      if (form.documentPaths.length > 0) {
        fd.append('documentPaths', JSON.stringify(form.documentPaths));
      }
      // Prepare remarks - if stakeholder name exists, prepend it to remarks
      let remarksToSave = form.remarks || '';
      if (form.stakeholderName && form.stakeholderName.trim() !== '') {
        const stakeholderPrefix = `STAKEHOLDER_NAME: ${form.stakeholderName.trim()}`;
        remarksToSave = remarksToSave ? `${stakeholderPrefix}\n\n${remarksToSave}` : stakeholderPrefix;
      }
      if (remarksToSave) fd.append('remarks', remarksToSave);
      if (form.statusId) fd.append('statusId', String(form.statusId));
      // Append task completion date if provided (for Completed status)
      if (form.taskCompletionDate) {
        // Ensure date is in YYYY-MM-DD format (HTML date input already provides this)
        fd.append('taskCompletionDate', String(form.taskCompletionDate));
      }

      let data;
      if (editingTaskId) {
        // Update existing task
        toast.loading('Updating task...', { id: 'updating-task' });
        const { data: updateData } = await axios.put(`/api/tasks/${editingTaskId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        data = updateData;
        toast.success('Task updated successfully!', { id: 'updating-task' });
        setEditingTaskId(null);
        resetForm();
      } else { 
        // Create new task
        toast.loading('Saving task...', { id: 'saving-task' });
        const { data: createData } = await axios.post('/api/tasks', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        data = createData;
        toast.success('Task saved successfully!', { id: 'saving-task' });
        resetForm();
      }

      setActiveTab('saved');
      await Promise.all([loadSaved(), loadExistingBuildings()]);

      if (submitAfterCreate) {
        const taskIdToSubmit = editingTaskId || data?.taskId;
        if (taskIdToSubmit) {
          await axios.post(`/api/tasks/${taskIdToSubmit}/submit`);
          toast.success('Task submitted');
          await Promise.all([loadSubmitted(), loadSaved(), loadExistingBuildings()]);
          setActiveTab('submitted');
          setEditingTaskId(null);
          resetForm();
        }
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.error || e?.message || 'Failed to save task';
      const detailedMsg = e?.response?.data?.details 
        ? `${errorMsg}: ${e.response.data.details}` 
        : errorMsg;
      
      toast.error(detailedMsg, {
        duration: 5000,
        style: {
          background: '#ef4444',
          color: '#fff',
          padding: '16px',
          borderRadius: '8px',
        }
      });
      
      // Log error for debugging
      console.error('Error saving task:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (task) => {
    // Format dates for HTML date input (YYYY-MM-DD), handling timezone safely
    const formatDateForInput = (value) => {
      if (!value) return '';

      // Normalize to string
      const str = String(value).trim();

      // If it already looks like YYYY-MM-DD, keep exactly that
      const ymdMatch = str.match(/^(\d{4}-\d{2}-\d{2})$/);
      if (ymdMatch) {
        return ymdMatch[1];
      }

      // If it's an ISO timestamp (e.g. 2026-02-03T18:30:00.000Z), parse and use LOCAL date,
      // so a UTC evening time correctly appears as the next day in India.
      if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
        const d = new Date(str);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }

      // Fallback: last attempt using Date
      const d = new Date(str);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      return '';
    };

    // Debug: Log training dates to see what we're getting
    console.log('Task data when editing:', {
      training_start_date: task.training_start_date,
      training_district: task.training_district,
      training_venue: task.training_venue,
      training_total_attendees: task.training_total_attendees,
      trainingId: task.training_master_id || task.training_id,
      taskId: task.id
    });
    const formattedStartDate = formatDateForInput(task.training_start_date);
    const formattedEndDate = formatDateForInput(task.training_end_date);
    // Task completion date is stored in task_date when status is Completed
    const formattedTaskCompletionDate = formatDateForInput(task.task_date);
   
    // Convert task_date to week format (YYYY-W##) using cell-specific week numbering
    // Note: If status is Completed, task_date contains the completion date, but we still
    // convert it to week format to show the week containing that completion date
    let taskDateValue = '';
    if (task.task_date) {
      // Parse date string as local date (YYYY-MM-DD format) to avoid timezone issues
      const dateParts = String(task.task_date).split('T')[0].split('-');
      if (dateParts.length !== 3) {
        taskDateValue = '';
      } else {
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2]);
        const date = new Date(year, month, day);
        
        if (isNaN(date.getTime())) {
          taskDateValue = '';
        } else {
          // Get Monday of the week containing the date
          const dayOfWeek = date.getDay();
          const daysToMonday = dayOfWeek === 0 ? 1 : -(dayOfWeek - 1);
          const monday = new Date(date);
          monday.setDate(date.getDate() + daysToMonday);
          monday.setHours(0, 0, 0, 0); // Normalize to midnight
      
      // Calculate ISO week number
      const weekNumber = calculateISOWeekNumberForEdit(monday, monday.getFullYear());
      
          taskDateValue = `${monday.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
        }
      }
    }

    setForm({
      quarterId: task.quarter_id || '',
      taskDate: taskDateValue,
      taskId: task.task_id || '',
      subtaskId: task.subtask_id || '',
      trainingId: task.training_master_id || task.training_id || '',
      training: {
        startDate: formattedStartDate,
        district: task.training_district || '',
        venue: task.training_venue || '',
        totalAttendees: task.training_total_attendees || '',
        totalWomen: task.training_total_women || '',
        totalMen: task.training_total_men || '',
        govtAttendees: task.training_govt_attendees || '',
        privateAttendees: task.training_private_attendees || '',
        professionalAttendees: task.training_professional_attendees || '',
        architectsCount: task.training_architects_count || '',
        studentAttendees: task.training_student_attendees || '',
        otherAttendees: task.training_other_attendees || '',
        modeId: task.training_mode_id || '',
      },
      buildingProjectId: task.task_no === 5 ? '' : (task.building_project_id ? String(task.building_project_id) : ''),
      buildingStatusId: task.task_no === 5 ? '' : (task.building_status_id ? String(task.building_status_id) : ''),
      buildingDetails: {
        name: task.task_no === 5 ? '' : (task.building_name || ''),
        address: task.task_no === 5 ? '' : (task.building_address || ''),
        stateId: task.task_no === 5 ? '' : (task.building_state_id ? String(task.building_state_id) : ''),
        districtId: task.task_no === 5 ? '' : (task.building_district_id ? String(task.building_district_id) : ''),
        latitude: task.task_no === 5 ? '' : (task.building_latitude || ''),
        longitude: task.task_no === 5 ? '' : (task.building_longitude || ''),
        nodalContactName: task.task_no === 5 ? '' : (task.building_nodal_contact_name || ''),
        nodalDesignation: task.task_no === 5 ? '' : (task.building_nodal_department || ''),
        nodalEmail: task.task_no === 5 ? '' : (task.building_nodal_email || ''),
        nodalContactNumber: task.task_no === 5 ? '' : (task.building_nodal_contact_number || ''),
        statusId: task.task_no === 5 ? '' : (task.building_status_id ? String(task.building_status_id) : ''),
        typology: task.task_no === 5 ? '' : (task.building_typology || ''),
        epiValueBase: task.task_no === 5 ? '' : (task.building_epi_value_base || ''),
        epiValueProposed: task.task_no === 5 ? '' : (task.building_epi_value_proposed || ''),
        epiRatio: task.task_no === 5 ? '' : (task.building_epi_ratio || ''),
      },
      buildingLabellingProjectId: task.building_labelling_project_id || '',
      buildingLabellingStatusId: task.building_labelling_status_id || '',
      buildingLabellingDetails: {
        name: task.building_labelling_name || '',
        address: task.building_labelling_address || '',
        stateId: task.building_labelling_state_id || '',
        districtId: task.building_labelling_district_id || '',
        latitude: task.building_labelling_latitude || '',
        longitude: task.building_labelling_longitude || '',
        nodalContactName: task.building_labelling_nodal_contact_name || '',
        nodalDesignation: task.building_labelling_nodal_department || '',
        nodalEmail: task.building_labelling_nodal_email || '',
        nodalContactNumber: task.building_labelling_nodal_contact_number || '',
        statusId: task.building_labelling_status_id || '',
      },
      buildingTask5ProjectId: task.task_no === 5 ? (task.building_project_id || '') : '',
      buildingTask5Details: {
        name: task.task_no === 5 ? (task.building_name || '') : '',
        address: task.task_no === 5 ? (task.building_address || '') : '',
        stateId: task.task_no === 5 ? (task.building_state_id || '') : '',
        districtId: task.task_no === 5 ? (task.building_district_id || '') : '',
        latitude: task.task_no === 5 ? (task.building_latitude || '') : '',
        longitude: task.task_no === 5 ? (task.building_longitude || '') : '',
        nodalContactName: task.task_no === 5 ? (task.building_nodal_contact_name || '') : '',
        nodalDesignation: task.task_no === 5 ? (task.building_nodal_department || '') : '',
        nodalEmail: task.task_no === 5 ? (task.building_nodal_email || '') : '',
        nodalContactNumber: task.task_no === 5 ? (task.building_nodal_contact_number || '') : '',
        statusId: task.task_no === 5 ? (task.building_status_id || '') : '',
      },
      buildingTask5Subtask1Details: {
        name: task.task_no === 5 && task.subtask_no === 1 ? (task.building_name || '') : '',
        address: task.task_no === 5 && task.subtask_no === 1 ? (task.building_address || '') : '',
        buildingConstructionStatus: task.task_no === 5 && task.subtask_no === 1 ? (task.building_status_id || '') : '',
        stateId: task.task_no === 5 && task.subtask_no === 1 ? (task.building_state_id ? String(task.building_state_id) : '') : '',
        districtId: task.task_no === 5 && task.subtask_no === 1 ? (task.building_district_id ? String(task.building_district_id) : '') : '',
        latitude: task.task_no === 5 && task.subtask_no === 1 ? (task.building_latitude || '') : '',
        longitude: task.task_no === 5 && task.subtask_no === 1 ? (task.building_longitude || '') : '',
      },
      buildingAction: task.task_no === 5 && task.subtask_no === 1 && task.building_id ? 'update' : 'new',
      newStatusId: task.task_no === 5 && task.subtask_no === 1 && task.building_id ? (task.building_status_id || '') : '',
      buildingTask5Subtask2Details: {
        name: task.task_no === 5 && task.subtask_no === 2 ? (task.building_name || '') : '',
        address: task.task_no === 5 && task.subtask_no === 2 ? (task.building_address || '') : '',
        districtId: task.task_no === 5 && task.subtask_no === 2 ? (task.building_district_id ? String(task.building_district_id) : '') : '',
        complianceLevel: task.task_no === 5 && task.subtask_no === 2 ? (task.compliance_level || '') : '',
        typology: task.task_no === 5 && task.subtask_no === 2 ? (task.building_typology || '') : '',
        ulbName: task.task_no === 5 && task.subtask_no === 2 ? (task.building_nodal_department || '') : '',
        epiBase: task.task_no === 5 && task.subtask_no === 2 ? (task.building_epi_value_base || '') : '',
        epiProposed: task.task_no === 5 && task.subtask_no === 2 ? (task.building_epi_value_proposed || '') : '',
        epiRatio: task.task_no === 5 && task.subtask_no === 2 ? (task.building_epi_ratio || '') : '',
        latitude: task.task_no === 5 && task.subtask_no === 2 ? (task.building_latitude || '') : '',
        longitude: task.task_no === 5 && task.subtask_no === 2 ? (task.building_longitude || '') : '',
        statusId: task.task_no === 5 && task.subtask_no === 2 ? (task.building_status_id || '') : '',
      },
      buildingTask5Subtask2Action: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? 'update' : 'new',
      existingBuildingTask5Subtask2Id: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_id || null) : null,
      updateBuildingTask5Subtask2Name: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_name || '') : '',
      updateBuildingTask5Subtask2Address: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_address || '') : '',
      updateBuildingTask5Subtask2DistrictId: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_district_id ? String(task.building_district_id) : '') : '',
      updateBuildingTask5Subtask2ComplianceLevel: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.compliance_level || '') : '',
      updateBuildingTask5Subtask2Typology: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_typology || '') : '',
      updateBuildingTask5Subtask2UlbName: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_nodal_department || '') : '',
      updateBuildingTask5Subtask2EpiBase: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_epi_value_base || '') : '',
      updateBuildingTask5Subtask2EpiProposed: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_epi_value_proposed || '') : '',
      updateBuildingTask5Subtask2EpiRatio: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_epi_ratio || '') : '',
      updateBuildingTask5Subtask2Latitude: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_latitude || '') : '',
      updateBuildingTask5Subtask2Longitude: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_longitude || '') : '',
      updateBuildingTask5Subtask2StatusId: task.task_no === 5 && task.subtask_no === 2 && task.building_id ? (task.building_status_id || '') : '',
      remarks: (() => {
        // Extract stakeholder name from remarks if present
        const remarksText = task.remarks || '';
        if (remarksText.startsWith('STAKEHOLDER_NAME:')) {
          const lines = remarksText.split('\n');
          // Return remarks without stakeholder name prefix (skip first line and any empty lines after)
          return lines.slice(1).join('\n').replace(/^\n+/, '');
        }
        return remarksText;
      })(),
      statusId: task.status_id || '',
      taskCompletionDate: formattedTaskCompletionDate || '',
      stakeholderName: (() => {
        // Extract stakeholder name from remarks if present
        const remarksText = task.remarks || '';
        if (remarksText.startsWith('STAKEHOLDER_NAME:')) {
          const lines = remarksText.split('\n');
          const stakeholderLine = lines[0];
          return stakeholderLine.replace('STAKEHOLDER_NAME:', '').trim();
        }
        return '';
      })(),
      isNotified: task.is_notified || '',
      notificationDate: task.notification_date || '',
      incorporationInByLaws: task.incorporation_in_by_laws || '',
      documents: [], // File inputs can't be prefilled
      documentPaths: (() => {
        // Handle both old format (single string) and new format (JSON array)
        if (!task.document_path) return [];
        try {
          const parsed = JSON.parse(task.document_path);
          return Array.isArray(parsed) ? parsed : [task.document_path];
        } catch (e) {
          // Not JSON, treat as single path string (old format)
          return [task.document_path];
        }
      })(), // Store existing document path(s) as array
      buildingAction: 'new', // Always show full form when editing
      existingBuildingId: task.building_id || null, // Keep track of existing building ID for updates
    });
    setEditingTaskId(task.id);
    setActiveTab('add');
  };

  const handleSubmitTask = async (taskId) => {
    try {
      await axios.post(`/api/tasks/${taskId}/submit`);
      toast.success('Submitted');
      await Promise.all([loadSaved(), loadSubmitted()]);
    } catch (error) {
      toast.error('Failed to submit task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`/api/tasks/${taskId}`);
      toast.success('Deleted');
      await loadSaved();
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.details || error.message || 'Failed to delete task';
      toast.error(msg);
    }
  };

  const resetForm = () => {
    setForm((prev) => ({
      ...prev,
      quarterId: '',
      taskDate: '',
      taskId: '',
      subtaskId: '',
      isNotified: '',
      notificationDate: '',
      incorporationInByLaws: '',
      trainingId: '',
      training: {
        startDate: '', district: '', venue: '', totalAttendees: '', totalWomen: '', totalMen: '', govtAttendees: '', privateAttendees: '', professionalAttendees: '', architectsCount: '', studentAttendees: '', otherAttendees: '', modeId: '',
      },
      buildingProjectId: '',
      buildingStatusId: '',
      buildingDetails: { name: '', address: '', stateId: '', districtId: '', latitude: '', longitude: '', nodalContactName: '', nodalDesignation: '', nodalEmail: '', nodalContactNumber: '', statusId: '', typology: '', epiValueBase: '', epiValueProposed: '', epiRatio: '' },
      buildingLabellingProjectId: '',
      buildingLabellingStatusId: '',
      buildingLabellingDetails: { name: '', address: '', stateId: '', districtId: '', latitude: '', longitude: '', nodalContactName: '', nodalDesignation: '', nodalEmail: '', nodalContactNumber: '', statusId: '' },
      buildingTask5ProjectId: '',
      buildingTask5Details: { name: '', address: '', stateId: '', districtId: '', latitude: '', longitude: '', nodalContactName: '', nodalDesignation: '', nodalEmail: '', nodalContactNumber: '', statusId: '' },
      buildingTask5Subtask1Details: { name: '', address: '', buildingConstructionStatus: '', stateId: '', districtId: '', latitude: '', longitude: '' },
      buildingTask5Subtask2Details: { 
        name: '', 
        address: '', 
        districtId: '', 
        complianceLevel: '', 
        typology: '', 
        ulbName: '', 
        epiBase: '', 
        epiProposed: '',
        epiRatio: '',
        latitude: '',
        longitude: '',
        statusId: ''
      },
      remarks: '',
      statusId: '',
      documents: [],
      documentPaths: [],
      buildingAction: 'new',
      existingBuildingId: null,
      newStatusId: '',
      newDistrictId: '',
      newLatitude: '',
      newLongitude: '',
      updateBuildingName: '',
      updateBuildingAddress: '',
      buildingTask5Subtask2Action: 'new',
      existingBuildingTask5Subtask2Id: null,
      updateBuildingTask5Subtask2Name: '',
      updateBuildingTask5Subtask2Address: '',
      updateBuildingTask5Subtask2DistrictId: '',
      updateBuildingTask5Subtask2ComplianceLevel: '',
      updateBuildingTask5Subtask2Typology: '',
      updateBuildingTask5Subtask2UlbName: '',
      updateBuildingTask5Subtask2EpiBase: '',
      updateBuildingTask5Subtask2EpiProposed: '',
      updateBuildingTask5Subtask2EpiRatio: '',
      updateBuildingTask5Subtask2Latitude: '',
      updateBuildingTask5Subtask2Longitude: '',
      updateBuildingTask5Subtask2StatusId: '',
    }));
    setEditingTaskId(null);
  };

  // Event handlers extracted from inline handlers for CSP compliance
  const handleQuarterChange = createFormFieldHandler(setForm, 'quarterId');
  
  const handleWeekPickerToggle = createToggleHandler(setShowWeekPicker);
  
  const handleMonthPrev = createMonthNavigationHandler(setWeekPickerMonth, weekPickerMonth, 'prev');
  
  const handleMonthNext = createMonthNavigationHandler(setWeekPickerMonth, weekPickerMonth, 'next');
  
  const handleWeekSelect = (weekStr) => {
    return () => {
      setForm(prev => ({ ...prev, taskDate: weekStr }));
      setShowWeekPicker(false);
    };
  };
  
  const handleWeekClear = () => {
    setForm(prev => ({ ...prev, taskDate: '' }));
    setShowWeekPicker(false);
  };
  
  const handleThisWeek = () => {
    const today = new Date();
    const todayWeeks = getWeeksForMonth(today);
    const todayWeek = todayWeeks.find(w => 
      w.monday <= today && w.sunday >= today
    );
    if (todayWeek) {
      const weekStr = `${todayWeek.monday.getFullYear()}-W${String(todayWeek.cellWeekNumber).padStart(2, '0')}`;
      setForm(prev => ({ ...prev, taskDate: weekStr }));
    }
    setShowWeekPicker(false);
  };
  
  const handleTaskCompletionDateChange = createFormFieldHandler(setForm, 'taskCompletionDate');
  
  const handleTaskSelect = createSelectHandler(setForm, 'taskId', 'subtaskId');
  
  const handleSubtaskSelect = createSelectHandler(setForm, 'subtaskId');
  
  const handleTrainingFieldChange = (field) => createNestedFormFieldHandler(setForm, 'training', field);
  
  const handleBuildingFieldChange = (field) => createNestedFormFieldHandler(setForm, 'buildingDetails', field);
  
  const handleBuildingLabellingFieldChange = (field) => createNestedFormFieldHandler(setForm, 'buildingLabellingDetails', field);
  
  const handleBuildingTask5FieldChange = (field) => createNestedFormFieldHandler(setForm, 'buildingTask5Details', field);
  
  const handleBuildingTask5Subtask1FieldChange = (field) => createNestedFormFieldHandler(setForm, 'buildingTask5Subtask1Details', field);

  const handleBuildingTask5Subtask2FieldChange = (field) => createNestedFormFieldHandler(setForm, 'buildingTask5Subtask2Details', field);
  
  const handleEpiValueBaseChange = (e) => {
    const baseValue = e.target.value;
    const proposedValue = form.buildingDetails.epiValueProposed;
    let calculatedRatio = '';
    if (baseValue && proposedValue) {
      const base = parseFloat(baseValue);
      const proposed = parseFloat(proposedValue);
      if (!isNaN(base) && !isNaN(proposed) && base !== 0) {
        calculatedRatio = (proposed / base).toFixed(4);
      }
    }
    setForm(prev => ({
      ...prev,
      buildingDetails: {
        ...prev.buildingDetails,
        epiValueBase: baseValue,
        epiRatio: calculatedRatio
      }
    }));
  };
  
  const handleEpiValueProposedChange = (e) => {
    const proposedValue = e.target.value;
    const baseValue = form.buildingDetails.epiValueBase;
    let calculatedRatio = '';
    if (baseValue && proposedValue) {
      const base = parseFloat(baseValue);
      const proposed = parseFloat(proposedValue);
      if (!isNaN(base) && !isNaN(proposed) && base !== 0) {
        calculatedRatio = (proposed / base).toFixed(4);
      }
    }
    setForm(prev => ({
      ...prev,
      buildingDetails: {
        ...prev.buildingDetails,
        epiValueProposed: proposedValue,
        epiRatio: calculatedRatio
      }
    }));
  };

  const FormSection = (
    <>
    <div className="card shadow-lg">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="flex flex-col">
          <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            Quarter <span className="text-red-500">*</span>
          </label>
          <div className="flex-1">
            <select 
              className="w-full input-field border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg transition-all h-[42px]" 
              value={form.quarterId} 
              onChange={handleQuarterChange}
            >
              <option value="">Select Quarter</option>
              {dropdowns.quarters.map((q) => (
                <option key={q.id} value={q.id}>{q.quarter_name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-col">
          <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            Task Week <span className="text-red-500">*</span>
          </label>
          <div className="flex-1 relative" ref={weekPickerRef}>
            <button
              type="button"
              onClick={handleWeekPickerToggle}
              className="w-full input-field border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg transition-all text-left flex items-center justify-between h-[42px]"
            >
              <span className={form.taskDate ? 'text-gray-900' : 'text-gray-400'}>
                {form.taskDate ? getWeekFromDate(form.taskDate) : 'Select Week'}
              </span>
              <Calendar className="h-4 w-4 text-gray-400" />
            </button>
            {showWeekPicker && (
              <div className="absolute z-50 mt-2 bg-white border-2 border-blue-300 rounded-lg shadow-xl p-4 week-picker-modal">
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={handleMonthPrev}
                    disabled={(() => {
                      if (!user?.cellEstablishmentDate) return false;
                      const estDate = new Date(user.cellEstablishmentDate);
                      const estMonth = new Date(estDate.getFullYear(), estDate.getMonth(), 1);
                      const pickerMonth = new Date(weekPickerMonth.getFullYear(), weekPickerMonth.getMonth(), 1);
                      return pickerMonth <= estMonth;
                    })()}
                    className={`p-1 rounded ${
                      (() => {
                        if (!user?.cellEstablishmentDate) return 'hover:bg-gray-100';
                        const estDate = new Date(user.cellEstablishmentDate);
                        const estMonth = new Date(estDate.getFullYear(), estDate.getMonth(), 1);
                        const pickerMonth = new Date(weekPickerMonth.getFullYear(), weekPickerMonth.getMonth(), 1);
                        return pickerMonth <= estMonth ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100';
                      })()
                    }`}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <div className="font-semibold">
                    {weekPickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </div>
                  <button
                    type="button"
                    onClick={handleMonthNext}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
                <div className="space-y-2">
                  {getWeeksForMonth(weekPickerMonth).map((week, idx) => {
                    // Use cell-specific week number for storage (not ISO week)
                    const weekStr = `${week.monday.getFullYear()}-W${String(week.cellWeekNumber).padStart(2, '0')}`;
                    const isSelected = form.taskDate === weekStr;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={handleWeekSelect(weekStr)}
                        className={`w-full text-left p-3 rounded border-2 transition-all ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-blue-600">
                              Week {week.cellWeekNumber}
                            </div>
                            <div className="text-xs text-gray-600">
                              {week.monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {week.sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          </div>
                          {isSelected && <CheckCircle2 className="h-5 w-5 text-blue-600" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t flex justify-between">
                  <button
                    type="button"
                    onClick={handleWeekClear}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={handleThisWeek}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    This Week
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col">
          <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            Task Completion Date {(() => {
              const selectedStatus = dropdowns.statuses.find(s => String(s.status_id) === String(form.statusId));
              const isCompleted = selectedStatus && selectedStatus.status && selectedStatus.status.toLowerCase().trim() === 'completed';
              return isCompleted ? <span className="text-red-500">*</span> : null;
            })()}
          </label>
          <div className="flex-1">
            <input
              type="date"
              className="w-full input-field border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg transition-all h-[42px]"
              value={form.taskCompletionDate || ''}
              max={new Date().toISOString().split('T')[0]}
              onChange={handleTaskCompletionDateChange}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col mb-6">
        <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-2">
            <Target className="h-4 w-4 text-blue-600" />
            Task <span className="text-red-500">*</span>
          </label>
          <div className="flex-1">
          <Select
            classNamePrefix="rt"
            isSearchable
            value={(() => {
              const found = (dropdowns.tasks || []).find((t) => String(t.task_id) === String(form.taskId));
              return found ? { value: found.task_id, label: `Task ${found.task_no}: ${found.task_description}`, taskNo: found.task_no } : null;
            })()}
            onChange={handleTaskSelect}
            options={(dropdowns.tasks || []).map((t) => ({ 
              value: t.task_id, 
              label: `Task ${t.task_no}: ${t.task_description}`, 
              taskNo: t.task_no,
              isDisabled: isTask4Disabled && Number(t.task_no) === 4
            }))}
            isOptionDisabled={(option) => option.isDisabled}
            formatOptionLabel={({ label, taskNo, isDisabled }) => {
              const labelParts = label.split(': ');
              return (
                <span className={isDisabled ? 'select-option-disabled' : ''}>
                  <span className="select-option-bold" style={{ '--task-color': getTaskColor(taskNo), color: getTaskColor(taskNo) }}>
                    Task {taskNo}:
                  </span>
                  <span> {labelParts[1] || labelParts[0]}</span>
                  {isDisabled && <span className="select-option-error">(Not available from Q4 onwards)</span>}
                </span>
              );
            }}
            styles={{
              option: (base, state) => ({ 
                ...base, 
                whiteSpace: 'normal',
                backgroundColor: state.isSelected ? '#e0e7ff' : state.isFocused ? '#f3f4f6' : 'white',
                color: state.isDisabled ? '#9ca3af' : '#374151',
                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                padding: '10px 12px',
                opacity: state.isDisabled ? 0.6 : 1
              }),
              valueContainer: (base) => ({
                ...base,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                flexWrap: 'wrap'
              }),
              input: (base) => ({
                ...base,
                margin: '0px',
                paddingBottom: '0px',
                paddingTop: '0px',
                visibility: 'hidden',
                height: '0'
              }),
              singleValue: (base) => ({ 
                ...base, 
                whiteSpace: 'normal !important',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                position: 'relative',
                transform: 'none',
                top: '0',
                left: '0',
                maxWidth: '100%'
              }),
              control: (base, state) => ({ 
                ...base, 
                minHeight: 42, 
                border: '2px solid #e5e7eb', 
                borderRadius: '0.5rem'
              }),
            }}
            components={{
              SingleValue: ({ data, innerProps }) => {
                const taskNo = data?.taskNo;
                const labelParts = data?.label?.split(': ') || [];
                return (
                  <div {...innerProps} className="select-menu-normal">
                    <span className="select-option-bold" style={{ '--task-color': getTaskColor(taskNo), color: getTaskColor(taskNo) }}>
                      {labelParts[0]}
                    </span>
                    {labelParts[1] && <span> {labelParts[1]}</span>}
                  </div>
                );
              }
            }}
            placeholder="Select Task"
          />
          </div>
        </div>
        <div className="flex flex-col mb-6">
          <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-2">
            <Target className="h-4 w-4 text-blue-600" />
            Subtask <span className="text-red-500">*</span>
          </label>
          <div className="flex-1">
          <Select
            classNamePrefix="rt"
            isSearchable
            value={(() => {
              const found = (subtasksForTask || []).find((s) => String(s.subtask_id) === String(form.subtaskId));
              return found ? { value: found.subtask_id, label: `Subtask ${found.subtask_no}: ${found.subtask_description}` } : null;
            })()}
            onChange={handleSubtaskSelect}
            options={(subtasksForTask || []).map((s) => ({ value: s.subtask_id, label: `Subtask ${s.subtask_no}: ${s.subtask_description}` }))}
            formatOptionLabel={({ label }) => {
              const labelParts = label.split(': ');
              const subtaskNo = labelParts[0];
              const description = labelParts[1] || labelParts[0];
              
              // Pattern to match important terms that indicate what needs to be uploaded/submitted
              const boldPatterns = [
                // ECSBC patterns (various formats)
                /(report on\s+)?(ECSBC[-–—]\s*(?:Commercial|Residential|C|R)(?:\s+projects)?)/gi,
                /ECSBC[-–—]\s*(?:C|R|Commercial|Residential)(?:\s+\(ENS\))?/gi,
                /ECSBC\s*&\s*ENS/gi,
                /ECSBC\s+compliance\s+status/gi,
                // Training patterns
                /Training[-–—]\s*[A-C]/gi,
                /Training\s+Modules/gi,
                // Key document/submission types (what needs to be uploaded)
                /proceedings\s+of/gi,
                /(?:Techno[-–—]?commercial|feasibility)\s+report/gi,
                /design\s+charrette/gi,
                /posters\/signages/gi,
                /videos\s+on/gi,
                /(?:tip\s+sheet|presentations)/gi,
                /modules\s+for/gi,
                // Progress reports
                /(?:weekly|monthly|quarterly)\s+progress\s+report/gi,
                /progress\s+report/gi,
                /consultant'?s\s+attendance/gi,
                /desired\s+format/gi,
                // Building and project related
                /building\s+projects/gi,
                /Urban\s+Development/gi,
                /Town\s+&\s+Country\s+Planning\s+Department/gi,
                // Compliance and procedure related
                /State\s+specific/gi,
                /compliance\s+procedure/gi,
                /frequently\s+asked\s+questions/gi,
                /manual\s+on/gi,
                // Organizations
                /SDA\/BEE/gi,
                /SDA\s*\/\s*BEE/gi
              ];
              
              // Collect all matches from all patterns
              const matches = [];
              boldPatterns.forEach(pattern => {
                pattern.lastIndex = 0;
                let match;
                while ((match = pattern.exec(description)) !== null) {
                  matches.push({
                    index: match.index,
                    length: match[0].length,
                    text: match[0]
                  });
                }
              });
              
              // Sort matches by index
              matches.sort((a, b) => a.index - b.index);
              
              // Remove overlapping matches (keep the longer one)
              const filteredMatches = [];
              for (let i = 0; i < matches.length; i++) {
                const current = matches[i];
                const prev = filteredMatches[filteredMatches.length - 1];
                if (!prev || prev.index + prev.length <= current.index) {
                  filteredMatches.push(current);
                } else if (current.length > prev.length) {
                  filteredMatches[filteredMatches.length - 1] = current;
                }
              }
              
              // Split description by the matches and create bold parts
              const parts = [];
              let lastIndex = 0;
              let hasMatch = false;
              
              filteredMatches.forEach((match) => {
                hasMatch = true;
                // Add text before the match
                if (match.index > lastIndex) {
                  parts.push(description.substring(lastIndex, match.index));
                }
                // Add the bold match
                parts.push(<strong key={match.index}>{match.text}</strong>);
                lastIndex = match.index + match.length;
              });
              
              // Add remaining text
              if (lastIndex < description.length) {
                parts.push(description.substring(lastIndex));
              }
              
              // If no matches found, return original
              if (!hasMatch) {
                return (
                  <span>
                    <span style={{ fontWeight: '700' }}>{subtaskNo}:</span> {description}
                  </span>
                );
              }
              
              return (
                <span>
                  <span className="select-option-bold">{subtaskNo}:</span> {parts}
                </span>
              );
            }}
            styles={{
              option: (base, state) => ({ 
                ...base, 
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                backgroundColor: state.isSelected ? '#e0e7ff' : state.isFocused ? '#f3f4f6' : 'white',
                color: '#374151',
                cursor: 'pointer',
                padding: '10px 12px'
              }),
              valueContainer: (base) => ({
                ...base,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                flexWrap: 'wrap'
              }),
              input: (base) => ({
                ...base,
                margin: '0px',
                paddingBottom: '0px',
                paddingTop: '0px',
                visibility: 'hidden',
                height: '0'
              }),
              singleValue: (base) => ({ 
                ...base, 
                whiteSpace: 'normal !important',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                position: 'relative',
                transform: 'none',
                top: '0',
                left: '0',
                maxWidth: '100%'
              }),
              control: (base, state) => ({ 
                ...base, 
                minHeight: 42, 
                border: '2px solid #e5e7eb', 
                borderRadius: '0.5rem'
              }),
            }}
            components={{
              SingleValue: ({ data, innerProps }) => {
                const labelParts = data?.label?.split(': ') || [];
                const subtaskNo = labelParts[0];
                const description = labelParts[1] || labelParts[0];
                
                // Pattern to match important terms that indicate what needs to be uploaded/submitted
                const boldPatterns = [
                  // ECSBC patterns (various formats)
                  /(report on\s+)?(ECSBC[-–—]\s*(?:Commercial|Residential|C|R)(?:\s+projects)?)/gi,
                  /ECSBC[-–—]\s*(?:C|R|Commercial|Residential)(?:\s+\(ENS\))?/gi,
                  /ECSBC\s*&\s*ENS/gi,
                  /ECSBC\s+compliance\s+status/gi,
                  // Training patterns
                  /Training[-–—]\s*[A-C]/gi,
                  /Training\s+Modules/gi,
                  // Key document/submission types (what needs to be uploaded)
                  /proceedings\s+of/gi,
                  /(?:Techno[-–—]?commercial|feasibility)\s+report/gi,
                  /design\s+charrette/gi,
                  /posters\/signages/gi,
                  /videos\s+on/gi,
                  /(?:tip\s+sheet|presentations)/gi,
                  /modules\s+for/gi,
                  // Progress reports
                  /(?:weekly|monthly|quarterly)\s+progress\s+report/gi,
                  /progress\s+report/gi,
                  /consultant'?s\s+attendance/gi,
                  /desired\s+format/gi,
                  // Building and project related
                  /building\s+projects/gi,
                  /Urban\s+Development/gi,
                  /Town\s+&\s+Country\s+Planning\s+Department/gi,
                  // Compliance and procedure related
                  /State\s+specific/gi,
                  /compliance\s+procedure/gi,
                  /frequently\s+asked\s+questions/gi,
                  /manual\s+on/gi,
                  // Organizations
                  /SDA\/BEE/gi,
                  /SDA\s*\/\s*BEE/gi
                ];
                
                // Collect all matches from all patterns
                const matches = [];
                boldPatterns.forEach(pattern => {
                  pattern.lastIndex = 0;
                  let match;
                  while ((match = pattern.exec(description)) !== null) {
                    matches.push({
                      index: match.index,
                      length: match[0].length,
                      text: match[0]
                    });
                  }
                });
                
                // Sort matches by index
                matches.sort((a, b) => a.index - b.index);
                
                // Remove overlapping matches (keep the longer one)
                const filteredMatches = [];
                for (let i = 0; i < matches.length; i++) {
                  const current = matches[i];
                  const prev = filteredMatches[filteredMatches.length - 1];
                  if (!prev || prev.index + prev.length <= current.index) {
                    filteredMatches.push(current);
                  } else if (current.length > prev.length) {
                    filteredMatches[filteredMatches.length - 1] = current;
                  }
                }
                
                // Split description by the matches and create bold parts
                const parts = [];
                let lastIndex = 0;
                let hasMatch = false;
                
                filteredMatches.forEach((match) => {
                  hasMatch = true;
                  // Add text before the match
                  if (match.index > lastIndex) {
                    parts.push(description.substring(lastIndex, match.index));
                  }
                  // Add the bold match
                  parts.push(<strong key={match.index}>{match.text}</strong>);
                  lastIndex = match.index + match.length;
                });
                
                // Add remaining text
                if (lastIndex < description.length) {
                  parts.push(description.substring(lastIndex));
                }
                
                // If no matches found, return original
                if (!hasMatch) {
                  return (
                    <div {...innerProps} style={{ 
                      whiteSpace: 'normal', 
                      wordWrap: 'break-word', 
                      overflowWrap: 'break-word',
                      display: 'block',
                      width: '100%',
                      lineHeight: '1.5',
                      position: 'relative',
                      top: '0',
                      left: '0',
                      transform: 'none'
                    }}>
                      <span className="select-option-bold">{subtaskNo}:</span>
                      {description && <span> {description}</span>}
                    </div>
                  );
                }
                
                return (
                  <div {...innerProps} className="select-menu-normal">
                    <span className="select-option-bold">{subtaskNo}:</span>
                    {parts.length > 0 && <span> {parts}</span>}
                  </div>
                );
              }
            }}
            placeholder="Select Subtask"
          />
          </div>
        </div>

      {/* Notification fields for Task 1, Subtask 2b */}
      {showNotificationFields && (
        <div className="space-y-6 mb-6">
          {/* Is notified? with Date of Notification inline */}
          <div className="flex flex-col">
            <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-3">
              <CheckCircle2 className="h-4 w-4 text-blue-600" />
              Is notified? <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name="isNotified"
                    value="yes"
                    checked={form.isNotified === 'yes'}
                    onChange={(e) => setForm({ ...form, isNotified: e.target.value })}
                    className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                    required
                  />
                  <span className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name="isNotified"
                    value="no"
                    checked={form.isNotified === 'no'}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, isNotified: e.target.value, notificationDate: '' }));
                    }}
                    className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                    required
                  />
                  <span className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors">No</span>
                </label>
              </div>
              {form.isNotified === 'yes' && (
                <div className="flex items-center gap-3">
                  <label className="form-label flex items-center gap-2 text-gray-700 font-semibold whitespace-nowrap">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    Date of Notification <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="input-field border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg transition-all h-[42px] w-[200px]"
                    value={form.notificationDate}
                    onChange={createFormFieldHandler(setForm, 'notificationDate')}
                    required
                  />
                </div>
              )}
            </div>
          </div>
          
          {/* Incorporation in Building By-Laws */}
          <div className="flex flex-col">
            <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-3">
              <CheckCircle2 className="h-4 w-4 text-blue-600" />
              Incorporation in Building By-Laws <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="incorporationInByLaws"
                  value="yes"
                  checked={form.incorporationInByLaws === 'yes'}
                  onChange={createFormFieldHandler(setForm, 'incorporationInByLaws')}
                  className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                  required
                />
                <span className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors">Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="incorporationInByLaws"
                  value="no"
                  checked={form.incorporationInByLaws === 'no'}
                  onChange={createFormFieldHandler(setForm, 'incorporationInByLaws')}
                  className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                  required
                />
                <span className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors">No</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Subtask-specific forms - shown immediately after Subtask field */}
      {showTrainingDetails && (
        <div className="mt-6">
          <h3 className="text-md font-semibold mb-3">Training Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Training <span className="text-red-500">*</span></label>
              <select className="input-field" value={form.trainingId} onChange={createFormFieldHandler(setForm, 'trainingId')}>
                <option value="">Select Training</option>
                {filteredTrainings.map((t) => (
                  <option key={t.training_id} value={t.training_id}>{t.training_type} ({t.training_mode})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Date of Training <span className="text-red-500">*</span></label>
              <input type="date" className="input-field" value={form.training.startDate} onChange={handleTrainingFieldChange('startDate')} />
            </div>
            <div>
              <label className="form-label">District {isDistrictRequired && <span className="text-red-500">*</span>}</label>
              <select 
                className="input-field" 
                value={form.training.district} 
                onChange={handleTrainingFieldChange('district')}
                disabled={!userCellStateId}
                required={isDistrictRequired}
              >
                <option value="">Select District</option>
                {userCellStateId && dropdowns.districts
                  .filter((d) => String(d.state_id) === String(userCellStateId))
                  .map((d) => (
                    <option key={d.district_id} value={d.district_id}>{d.district_name}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="form-label">Venue</label>
              <input 
                type="text" 
                className="input-field" 
                value={form.training.venue} 
                onChange={handleTrainingFieldChange('venue')}
                placeholder="Enter venue"
              />
            </div>
            <div>
              <label className="form-label">Total No. of Attendees <span className="text-red-500">*</span></label>
              <input 
                type="number" 
                className="input-field" 
                value={form.training.totalAttendees} 
                onChange={(e) => {
                  const totalAttendees = e.target.value;
                  const totalAttendeesNum = Number(totalAttendees);
                  const totalWomenNum = Number(form.training.totalWomen) || 0;
                  const totalMenNum = Number(form.training.totalMen) || 0;
                  
                  let newTraining = { ...form.training, totalAttendees };
                  
                  // If Total Attendees is filled and valid, auto-calculate the missing gender field
                  if (totalAttendees && !isNaN(totalAttendeesNum) && totalAttendeesNum > 0) {
                    if (totalWomenNum > 0 && totalMenNum === 0) {
                      // If women is filled but men is not, calculate men
                      newTraining.totalMen = String(Math.max(0, totalAttendeesNum - totalWomenNum));
                    } else if (totalMenNum > 0 && totalWomenNum === 0) {
                      // If men is filled but women is not, calculate women
                      newTraining.totalWomen = String(Math.max(0, totalAttendeesNum - totalMenNum));
                    }
                  }
                  
                  setForm({ ...form, training: newTraining });
                }} 
              />
            </div>
            <div>
              <label className="form-label">Total No. of Female Participants <span className="text-red-500">*</span></label>
              <input 
                type="number" 
                className="input-field" 
                value={form.training.totalWomen} 
                onChange={(e) => {
                  const totalWomen = e.target.value;
                  const totalAttendeesNum = Number(form.training.totalAttendees) || 0;
                  const totalWomenNum = Number(totalWomen) || 0;
                  
                  let newTraining = { ...form.training, totalWomen };
                  
                  // If Total Attendees is filled and valid, auto-calculate men
                  if (form.training.totalAttendees && !isNaN(totalAttendeesNum) && totalAttendeesNum > 0 && totalWomen) {
                    if (!isNaN(totalWomenNum) && totalWomenNum >= 0) {
                      newTraining.totalMen = String(Math.max(0, totalAttendeesNum - totalWomenNum));
                    }
                  }
                  
                  setForm({ ...form, training: newTraining });
                }} 
              />
            </div>
            <div>
              <label className="form-label">Total No. of Male Participants <span className="text-red-500">*</span></label>
              <input 
                type="number" 
                className="input-field" 
                value={form.training.totalMen} 
                onChange={(e) => {
                  const totalMen = e.target.value;
                  const totalAttendeesNum = Number(form.training.totalAttendees) || 0;
                  const totalMenNum = Number(totalMen) || 0;
                  
                  let newTraining = { ...form.training, totalMen };
                  
                  // If Total Attendees is filled and valid, auto-calculate women
                  if (form.training.totalAttendees && !isNaN(totalAttendeesNum) && totalAttendeesNum > 0 && totalMen) {
                    if (!isNaN(totalMenNum) && totalMenNum >= 0) {
                      newTraining.totalWomen = String(Math.max(0, totalAttendeesNum - totalMenNum));
                    }
                  }
                  
                  setForm({ ...form, training: newTraining });
                }} 
              />
            </div>
            <div>
              <label className="form-label">Total No. of Government Sector Participants <span className="text-red-500">*</span></label>
              <input type="number" className="input-field" value={form.training.govtAttendees} onChange={(e) => setForm({ ...form, training: { ...form.training, govtAttendees: e.target.value } })} />
            </div>
            <div>
              <label className="form-label">Total No. of Private Sector Participants <span className="text-red-500">*</span></label>
              <input type="number" className="input-field" value={form.training.privateAttendees} onChange={(e) => setForm({ ...form, training: { ...form.training, privateAttendees: e.target.value } })} />
            </div>
            <div>
              <label className="form-label">Total No. of Professionals Participants <span className="text-red-500">*</span></label>
              <input type="number" className="input-field" value={form.training.professionalAttendees} onChange={(e) => setForm({ ...form, training: { ...form.training, professionalAttendees: e.target.value } })} />
            </div>
            <div>
              <label className="form-label">Total No. of Architects Participants <span className="text-red-500">*</span></label>
              <input type="number" className="input-field" value={form.training.architectsCount} onChange={(e) => setForm({ ...form, training: { ...form.training, architectsCount: e.target.value } })} />
            </div>
            <div>
              <label className="form-label">Total No. of Students Participants <span className="text-red-500">*</span></label>
              <input type="number" className="input-field" value={form.training.studentAttendees} onChange={(e) => setForm({ ...form, training: { ...form.training, studentAttendees: e.target.value } })} />
            </div>
            <div>
              <label className="form-label">Total No. of Other Participants <span className="text-red-500">*</span></label>
              <input type="number" className="input-field" value={form.training.otherAttendees} onChange={(e) => setForm({ ...form, training: { ...form.training, otherAttendees: e.target.value } })} />
            </div>
          </div>
        </div>
      )}

      {showBuildingDetailsLabellingFalse && (
        <div className="mt-6">
          <h3 className="text-md font-semibold mb-3">Building Project Details (Non-Labelling)</h3>
          
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <h4 className="font-semibold text-blue-800 mb-2">Building Action</h4>
            {buildingsForCurrentTask.length > 0 ? (
              <p className="text-blue-700 mb-3">You have {buildingsForCurrentTask.length} existing building(s) for this task/subtask combination.</p>
            ) : (
              <p className="text-blue-700 mb-3">No existing buildings found for this task/subtask combination.</p>
            )}
            <div className="flex gap-4">
              {(() => {
                
                if (existingBuildings.length > 0) {
                 
                }
                return null;
              })()}
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buildingAction"
                  value="update"
                  checked={form.buildingAction === 'update'}
                  onChange={(e) => {
                    setForm({ ...form, buildingAction: e.target.value, existingBuildingId: null });
                  }}
                  className="mr-2"
                />
                <span>Update an existing building</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buildingAction"
                  value="new"
                  checked={form.buildingAction === 'new'}
                  onChange={(e) => {
                    setForm(prev => ({ ...prev, buildingAction: e.target.value, existingBuildingId: null }));
                  }}
                  className="mr-2"
                />
                <span>Add new building</span>
              </label>
            </div>
          </div>

          {form.buildingAction === 'update' && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <h4 className="font-semibold text-blue-800 mb-2">Select Building to Update</h4>
              {buildingsForCurrentTask.length > 0 ? (
                <>
                  <select 
                    className="input-field mb-3"
                    value={form.existingBuildingId || ''}
                    onChange={(e) => {
                      const buildingId = e.target.value;
                      const selectedBuilding = buildingsForCurrentTask.find(b => String(b.building_id) === String(buildingId));
                      if (selectedBuilding) {
                        setForm({
                          ...form,
                          existingBuildingId: buildingId,
                          buildingDetails: {
                            ...form.buildingDetails,
                            name: selectedBuilding.building_name || '',
                            address: selectedBuilding.building_address || '',
                            stateId: selectedBuilding.building_state_id ? String(selectedBuilding.building_state_id) : '',
                            districtId: selectedBuilding.building_district_id ? String(selectedBuilding.building_district_id) : '',
                            latitude: selectedBuilding.latitude ? String(selectedBuilding.latitude) : '',
                            longitude: selectedBuilding.longitude ? String(selectedBuilding.longitude) : '',
                            nodalContactName: selectedBuilding.building_nodal_contact_name || '',
                            nodalDesignation: selectedBuilding.building_nodal_department || '',
                            nodalEmail: selectedBuilding.building_nodal_email || '',
                            nodalContactNumber: selectedBuilding.building_nodal_contact_number || '',
                            statusId: selectedBuilding.status_id ? String(selectedBuilding.status_id) : '',
                            typology: selectedBuilding.building_typology || '',
                            epiValueBase: selectedBuilding.building_epi_value_base ? String(selectedBuilding.building_epi_value_base) : '',
                            epiValueProposed: selectedBuilding.building_epi_value_proposed ? String(selectedBuilding.building_epi_value_proposed) : '',
                            epiRatio: selectedBuilding.building_epi_ratio ? String(selectedBuilding.building_epi_ratio) : '',
                          },
                          buildingProjectId: selectedBuilding.building_project_id ? String(selectedBuilding.building_project_id) : '',
                        });
                      } else {
                        setForm({ ...form, existingBuildingId: buildingId });
                      }
                    }}
                  >
                    <option value="">Select Building</option>
                    {buildingsForCurrentTask.map((building) => {
                      return (
                        <option key={building.building_id} value={building.building_id}>
                          {building.building_name} - {building.building_address}
                        </option>
                      );
                    })}
                  </select>
                </>
              ) : (
                <p className="text-blue-700 mb-3">
                  No buildings available for this task/subtask combination. Please add a building first.
                </p>
              )}
            </div>
          )}

          {(form.buildingAction === 'new' || (form.buildingAction === 'update' && form.existingBuildingId)) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <label className="form-label">Building Name <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.buildingDetails.name} onChange={handleBuildingFieldChange('name')} />
            </div>
            <div className="md:col-span-3">
              <label className="form-label">Building Address <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.buildingDetails.address} onChange={handleBuildingFieldChange('address')} />
            </div>
            <div>
              <label className="form-label">District <span className="text-red-500">*</span></label>
              <select 
                className="input-field" 
                value={form.buildingDetails.districtId} 
                onChange={handleBuildingFieldChange('districtId')}
                disabled={!userCellStateId}
              >
                <option value="">Select District</option>
                {userCellStateId && dropdowns.districts
                  .filter((d) => String(d.state_id) === String(userCellStateId))
                  .map((d) => (
                    <option key={d.district_id} value={d.district_id}>{d.district_name}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="form-label">Latitude <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.buildingDetails.latitude} onChange={handleBuildingFieldChange('latitude')} />
            </div>
            <div>
              <label className="form-label">Longitude <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.buildingDetails.longitude} onChange={handleBuildingFieldChange('longitude')} />
            </div>
            <div>
              <label className="form-label">Nodal Contact Person Name <span className="text-red-500">*</span></label>
              <input 
                className="input-field" 
                value={form.buildingDetails.nodalContactName} 
                onChange={handleBuildingFieldChange('nodalContactName')} 
              />
            </div>
            <div>
              <label className="form-label">Department <span className="text-red-500">*</span></label>
              <input 
                className="input-field" 
                value={form.buildingDetails.nodalDesignation} 
                onChange={handleBuildingFieldChange('nodalDesignation')} 
                placeholder=""
                required
              />
            </div>
            <div>
              <label className="form-label">Email ID <span className="text-red-500">*</span></label>
              <input 
                type="email"
                className="input-field" 
                value={form.buildingDetails.nodalEmail} 
                onChange={handleBuildingFieldChange('nodalEmail')} 
              />
            </div>
            <div>
              <label className="form-label">Contact Number</label>
              <input 
                type="tel"
                className="input-field" 
                value={form.buildingDetails.nodalContactNumber} 
                onChange={handleBuildingFieldChange('nodalContactNumber')} 
              />
            </div>
            <div>
              <label className="form-label">Building Status <span className="text-red-500">*</span></label>
              <select className="input-field" value={form.buildingDetails.statusId} onChange={handleBuildingFieldChange('statusId')}>
                <option value="">Select Building Status</option>
                {filteredBuildingStatusesFalse.map((s) => (
                  <option key={s.building_status_id} value={s.building_status_id}>{s.building_status}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Typology <span className="text-red-500">*</span></label>
              <select className="input-field" value={form.buildingDetails.typology} onChange={handleBuildingFieldChange('typology')}>
                <option value="">Select Typology</option>
                {filteredTypologies && filteredTypologies.map((typology) => (
                  <option key={typology.typologyid || typology.TypologyId} value={typology.typologyname || typology.TypologyName}>
                    {typology.typologyname || typology.TypologyName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">EPI Value Base</label>
              <input 
                type="number" 
                step="any"
                className="input-field" 
                value={form.buildingDetails.epiValueBase} 
                onChange={handleEpiValueBaseChange}
                placeholder="Enter EPI Value Base"
              />
            </div>
            <div>
              <label className="form-label">EPI Value Proposed</label>
              <input 
                type="number" 
                step="any"
                className="input-field" 
                value={form.buildingDetails.epiValueProposed} 
                onChange={handleEpiValueProposedChange}
                placeholder="Enter EPI Value Proposed"
              />
            </div>
            <div>
              <label className="form-label">EPI Ratio</label>
              <input 
                type="text" 
                className="input-field bg-gray-50 cursor-not-allowed" 
                value={form.buildingDetails.epiRatio || ''} 
                readOnly
              />
            </div>
            </div>
          )}
        </div>
      )}

      {showBuildingDetailsLabellingTrue && (
        <div className="mt-6">
          <h3 className="text-md font-semibold mb-3">Building Labelling Details (Labelling)</h3>
          
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <h4 className="font-semibold text-blue-800 mb-2">Building Action</h4>
            {buildingsForCurrentTask.length > 0 ? (
              <p className="text-blue-700 mb-3">You have {buildingsForCurrentTask.length} existing building(s) for this task/subtask combination.</p>
            ) : (
              <p className="text-blue-700 mb-3">No existing buildings found for this task/subtask combination.</p>
            )}
            <div className="flex gap-4">
              {buildingsForCurrentTask.length > 0 && (
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="buildingAction"
                    value="update"
                    checked={form.buildingAction === 'update'}
                    onChange={createFormFieldHandler(setForm, 'buildingAction')}
                    className="mr-2"
                  />
                  <span>Update existing building status</span>
                </label>
              )}
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buildingAction"
                  value="new"
                  checked={form.buildingAction === 'new'}
                  onChange={(e) => setForm({ ...form, buildingAction: e.target.value })}
                  className="mr-2"
                />
                <span>Add new building</span>
              </label>
            </div>
          </div>

          {form.buildingAction === 'update' && buildingsForCurrentTask.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <h4 className="font-semibold text-blue-800 mb-2">Select Building to Update</h4>
              <select 
                className="input-field mb-3"
                value={form.existingBuildingId || ''}
                onChange={(e) => setForm({ ...form, existingBuildingId: e.target.value })}
              >
                <option value="">Select Building</option>
                {buildingsForCurrentTask.map((building) => (
                  <option key={building.building_id} value={building.building_id}>
                    {building.building_name} - {building.building_address}
                  </option>
                ))}
              </select>
              
              {form.existingBuildingId && (
                <div className="mt-3">
                  <h5 className="font-semibold text-blue-700 mb-2">Building Details (Read Only)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div><strong>Name:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.building_name}</div>
                    <div><strong>Address:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.building_address}</div>
                    <div><strong>State:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.state}</div>
                    <div><strong>District:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.district}</div>
                    <div><strong>Project Type:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.project_type}</div>
                    <div><strong>Current Status:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.status}</div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="form-label">New Status</label>
                    <select 
                      className="input-field"
                      value={form.newStatusId || ''}
                      onChange={(e) => setForm({ ...form, newStatusId: e.target.value })}
                    >
                      <option value="">Select New Status</option>
                      {filteredBuildingStatusesTrue.map((s) => (
                        <option key={s.building_status_id} value={s.building_status_id}>{s.building_status}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {(form.buildingAction === 'new' || (form.buildingAction === 'update' && form.existingBuildingId)) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Labelling Project</label>
              <select className="input-field" value={form.buildingLabellingProjectId} onChange={createFormFieldHandler(setForm, 'buildingLabellingProjectId')}>
                <option value="">Select Labelling Project</option>
                {filteredBuildingProjectsTrue.map((p) => (
                  <option key={p.building_project_id} value={p.building_project_id}>{p.building_project_type}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Building Name <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.buildingLabellingDetails.name} onChange={handleBuildingLabellingFieldChange('name')} />
            </div>
            <div className="md:col-span-3">
              <label className="form-label">Building Address <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.buildingLabellingDetails.address} onChange={handleBuildingLabellingFieldChange('address')} />
            </div>
            <div>
              <label className="form-label">State</label>
              <select className="input-field" value={form.buildingLabellingDetails.stateId} onChange={(e) => {
                setForm(prev => ({
                  ...prev,
                  buildingLabellingDetails: {
                    ...prev.buildingLabellingDetails,
                    stateId: e.target.value,
                    districtId: ''
                  }
                }));
              }}>
                <option value="">Select State</option>
                {dropdowns.states.map((s) => (
                  <option key={s.state_id} value={s.state_id}>{s.state_ut}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">District <span className="text-red-500">*</span></label>
              <select className="input-field" value={form.buildingLabellingDetails.districtId} onChange={handleBuildingLabellingFieldChange('districtId')}>
                <option value="">Select District</option>
                {dropdowns.districts.filter((d) => String(d.state_id) === String(form.buildingLabellingDetails.stateId)).map((d) => (
                  <option key={d.district_id} value={d.district_id}>{d.district_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Latitude <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.buildingLabellingDetails.latitude} onChange={handleBuildingLabellingFieldChange('latitude')} />
            </div>
            <div>
              <label className="form-label">Longitude <span className="text-red-500">*</span></label>
              <input className="input-field" value={form.buildingLabellingDetails.longitude} onChange={handleBuildingLabellingFieldChange('longitude')} />
            </div>
            <div>
              <label className="form-label">Nodal Contact Person Name <span className="text-red-500">*</span></label>
              <input 
                className="input-field" 
                value={form.buildingLabellingDetails.nodalContactName} 
                onChange={handleBuildingLabellingFieldChange('nodalContactName')} 
              />
            </div>
            <div>
              <label className="form-label">Designation</label>
              <input 
                className="input-field" 
                value={form.buildingLabellingDetails.nodalDesignation} 
                onChange={handleBuildingLabellingFieldChange('nodalDesignation')} 
              />
            </div>
            <div>
              <label className="form-label">Email ID <span className="text-red-500">*</span></label>
              <input 
                type="email"
                className="input-field" 
                value={form.buildingLabellingDetails.nodalEmail} 
                onChange={handleBuildingLabellingFieldChange('nodalEmail')} 
              />
            </div>
            <div>
              <label className="form-label">Contact Number</label>
              <input 
                type="tel"
                className="input-field" 
                value={form.buildingLabellingDetails.nodalContactNumber} 
                onChange={handleBuildingLabellingFieldChange('nodalContactNumber')} 
              />
            </div>
            <div>
              <label className="form-label">Status <span className="text-red-500">*</span></label>
              <select className="input-field" value={form.buildingLabellingDetails.statusId} onChange={handleBuildingLabellingFieldChange('statusId')}>
                <option value="">Select Status</option>
                {filteredBuildingStatusesTrue.map((s) => (
                  <option key={s.building_status_id} value={s.building_status_id}>{s.building_status}</option>
                ))}
              </select>
            </div>
          </div>
          )}
        </div>
      )}

      {showBuildingDetailsTask5 && (
        <div className="mt-6">
          <h3 className="text-md font-semibold mb-3">Building Details (Task 5 - Other)</h3>
          
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <h4 className="font-semibold text-blue-800 mb-2">Building Action</h4>
            {buildingsForCurrentTask.length > 0 ? (
              <p className="text-blue-700 mb-3">You have {buildingsForCurrentTask.length} existing building(s) for this task/subtask combination.</p>
            ) : (
              <p className="text-blue-700 mb-3">No existing buildings found for this task/subtask combination.</p>
            )}
            <div className="flex gap-4">
              {buildingsForCurrentTask.length > 0 && (
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="buildingAction"
                    value="update"
                    checked={form.buildingAction === 'update'}
                    onChange={createFormFieldHandler(setForm, 'buildingAction')}
                    className="mr-2"
                  />
                  <span>Update existing building status</span>
                </label>
              )}
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buildingAction"
                  value="new"
                  checked={form.buildingAction === 'new'}
                  onChange={(e) => setForm({ ...form, buildingAction: e.target.value })}
                  className="mr-2"
                />
                <span>Add new building</span>
              </label>
            </div>
          </div>

          {form.buildingAction === 'update' && buildingsForCurrentTask.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <h4 className="font-semibold text-blue-800 mb-2">Select Building to Update</h4>
              <select 
                className="input-field mb-3"
                value={form.existingBuildingId || ''}
                onChange={(e) => setForm({ ...form, existingBuildingId: e.target.value })}
              >
                <option value="">Select Building</option>
                {buildingsForCurrentTask.map((building) => (
                  <option key={building.building_id} value={building.building_id}>
                    {building.building_name} - {building.building_address}
                  </option>
                ))}
              </select>
              
              {form.existingBuildingId && (
                <div className="mt-3">
                  <h5 className="font-semibold text-blue-700 mb-2">Building Details (Read Only)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div><strong>Name:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.building_name}</div>
                    <div><strong>Address:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.building_address}</div>
                    <div><strong>State:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.state}</div>
                    <div><strong>District:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.district}</div>
                    <div><strong>Project Type:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.project_type}</div>
                    <div><strong>Current Status:</strong> {buildingsForCurrentTask.find(b => b.building_id == form.existingBuildingId)?.status}</div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="form-label">New Status</label>
                    <select 
                      className="input-field"
                      value={form.newStatusId || ''}
                      onChange={(e) => setForm({ ...form, newStatusId: e.target.value })}
                    >
                      <option value="">Select New Status</option>
                      {filteredBuildingStatusesTask5.map((s) => (
                        <option key={s.building_status_id} value={s.building_status_id}>{s.building_status}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {(form.buildingAction === 'new' || (form.buildingAction === 'update' && form.existingBuildingId)) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Project Type</label>
                <select className="input-field" value={form.buildingTask5ProjectId} onChange={createFormFieldHandler(setForm, 'buildingTask5ProjectId')}>
                  <option value="">Select Project Type</option>
                  {filteredBuildingProjectsTask5.map((p) => (
                    <option key={p.building_project_id} value={p.building_project_id}>{p.building_project_type}</option>
                  ))}
                </select>
                {filteredBuildingProjectsTask5.length === 0 && (
                  <div className="text-sm text-red-600 mt-1">No project types available for Task 5 Subtask 2</div>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Building Name <span className="text-red-500">*</span></label>
                <input className="input-field" value={form.buildingTask5Details.name} onChange={handleBuildingTask5FieldChange('name')} />
              </div>
              <div className="md:col-span-3">
                <label className="form-label">Building Address <span className="text-red-500">*</span></label>
                <input className="input-field" value={form.buildingTask5Details.address} onChange={handleBuildingTask5FieldChange('address')} />
              </div>
              <div>
                <label className="form-label">State</label>
                <select className="input-field" value={form.buildingTask5Details.stateId} onChange={(e) => {
                  setForm(prev => ({
                    ...prev,
                    buildingTask5Details: {
                      ...prev.buildingTask5Details,
                      stateId: e.target.value,
                      districtId: ''
                    }
                  }));
                }}>
                  <option value="">Select State</option>
                  {dropdowns.states.map((s) => (
                    <option key={s.state_id} value={s.state_id}>{s.state_ut}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">District <span className="text-red-500">*</span></label>
                <select className="input-field" value={form.buildingTask5Details.districtId} onChange={handleBuildingTask5FieldChange('districtId')}>
                  <option value="">Select District</option>
                  {dropdowns.districts.filter((d) => String(d.state_id) === String(form.buildingTask5Details.stateId)).map((d) => (
                    <option key={d.district_id} value={d.district_id}>{d.district_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Latitude <span className="text-red-500">*</span></label>
                <input className="input-field" value={form.buildingTask5Details.latitude} onChange={handleBuildingTask5FieldChange('latitude')} />
              </div>
              <div>
                <label className="form-label">Longitude <span className="text-red-500">*</span></label>
                <input className="input-field" value={form.buildingTask5Details.longitude} onChange={handleBuildingTask5FieldChange('longitude')} />
              </div>
              <div>
                <label className="form-label">Nodal Contact Person Name <span className="text-red-500">*</span></label>
                <input 
                  className="input-field" 
                  value={form.buildingTask5Details.nodalContactName} 
                  onChange={handleBuildingTask5FieldChange('nodalContactName')} 
                />
              </div>
              <div>
                <label className="form-label">Designation</label>
                <input 
                  className="input-field" 
                  value={form.buildingTask5Details.nodalDesignation} 
                  onChange={handleBuildingTask5FieldChange('nodalDesignation')} 
                />
              </div>
              <div>
                <label className="form-label">Email ID <span className="text-red-500">*</span></label>
                <input 
                  type="email"
                  className="input-field" 
                  value={form.buildingTask5Details.nodalEmail} 
                  onChange={handleBuildingTask5FieldChange('nodalEmail')} 
                />
              </div>
              <div>
                <label className="form-label">Contact Number</label>
                <input 
                  type="tel"
                  className="input-field" 
                  value={form.buildingTask5Details.nodalContactNumber} 
                  onChange={handleBuildingTask5FieldChange('nodalContactNumber')} 
                />
              </div>
              <div>
                <label className="form-label">Status (Other) <span className="text-red-500">*</span></label>
                <select className="input-field" value={form.buildingTask5Details.statusId} onChange={handleBuildingTask5FieldChange('statusId')}>
                  <option value="">Select Status</option>
                  {filteredBuildingStatusesTask5.map((s) => (
                    <option key={s.building_status_id} value={s.building_status_id}>{s.building_status}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {showBuildingDetailsTask5Subtask1 && (
        <div className="mt-6">
          <h3 className="text-md font-semibold mb-3">Building Details (Task 5, Subtask 1 - ECSBC)</h3>
          
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <h4 className="font-semibold text-blue-800 mb-2">Building Action</h4>
            {buildingsForCurrentTask.length > 0 ? (
              <p className="text-blue-700 mb-3">You have {buildingsForCurrentTask.length} existing building(s) for this task/subtask combination.</p>
            ) : (
              <p className="text-blue-700 mb-3">No existing buildings found for this task/subtask combination.</p>
            )}
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buildingAction"
                  value="update"
                  checked={form.buildingAction === 'update'}
                  onChange={createFormFieldHandler(setForm, 'buildingAction')}
                  className="mr-2"
                />
                <span>Update an existing building</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buildingAction"
                  value="new"
                  checked={form.buildingAction === 'new'}
                  onChange={(e) => setForm({ ...form, buildingAction: e.target.value })}
                  className="mr-2"
                />
                <span>Add New Building</span>
              </label>
            </div>
          </div>

          {form.buildingAction === 'update' && buildingsForCurrentTask.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <h4 className="font-semibold text-blue-800 mb-2">Select Building to Update</h4>
              <select 
                className="input-field mb-3"
                value={form.existingBuildingId || ''}
                onChange={(e) => {
                  const selectedBuilding = buildingsForCurrentTask.find(b => b.building_id == e.target.value);
                  setForm({ 
                    ...form, 
                    existingBuildingId: e.target.value,
                    updateBuildingName: selectedBuilding?.building_name || '',
                    updateBuildingAddress: selectedBuilding?.building_address || '',
                    newDistrictId: selectedBuilding?.building_district_id || '',
                    newLatitude: selectedBuilding?.building_latitude || '',
                    newLongitude: selectedBuilding?.building_longitude || ''
                  });
                }}
              >
                <option value="">Select Building</option>
                {buildingsForCurrentTask.map((building) => (
                  <option key={building.building_id} value={building.building_id}>
                    {building.building_name} - {building.building_address}
                  </option>
                ))}
              </select>
              
              {form.existingBuildingId && (
                <div className="mt-3">
                  <h5 className="font-semibold text-gray-700 mb-2">Building Details</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-1">
                      <label className="form-label">Building Name <span className="text-red-500">*</span></label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.updateBuildingName || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingName: e.target.value })}
                        placeholder="Enter building name"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <label className="form-label">Building Address <span className="text-red-500">*</span></label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.updateBuildingAddress || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingAddress: e.target.value })}
                        placeholder="Enter building address"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <label className="form-label">District <span className="text-red-500">*</span></label>
                      <select 
                        className="input-field"
                        value={form.newDistrictId || ''}
                        onChange={(e) => setForm({ ...form, newDistrictId: e.target.value })}
                      >
                        <option value="">Select District</option>
                        {userCellStateId && dropdowns.districts
                          ?.filter((d) => String(d.state_id) === String(userCellStateId))
                          .map((d) => (
                            <option key={d.district_id} value={d.district_id}>
                              {d.district_name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Latitude</label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.newLatitude || ''}
                        onChange={(e) => setForm({ ...form, newLatitude: e.target.value })}
                        placeholder="Enter latitude (optional)"
                      />
                    </div>
                    <div>
                      <label className="form-label">Longitude</label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.newLongitude || ''}
                        onChange={(e) => setForm({ ...form, newLongitude: e.target.value })}
                        placeholder="Enter longitude (optional)"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="form-label">Building Construction Status <span className="text-red-500">*</span></label>
                      <select 
                        className="input-field"
                        value={form.newStatusId || ''}
                        onChange={(e) => setForm({ ...form, newStatusId: e.target.value })}
                      >
                        <option value="">Select Construction Status</option>
                        {filteredBuildingStatusesTask5Subtask1.map((s) => (
                          <option key={s.building_status_id} value={s.building_status_id}>{s.building_status}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {form.buildingAction === 'new' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="form-label">Building Name <span className="text-red-500">*</span></label>
                <input className="input-field" value={form.buildingTask5Subtask1Details.name} onChange={handleBuildingTask5Subtask1FieldChange('name')} placeholder="Enter building name" />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Building Address <span className="text-red-500">*</span></label>
                <input className="input-field" value={form.buildingTask5Subtask1Details.address} onChange={handleBuildingTask5Subtask1FieldChange('address')} placeholder="Enter building address" />
              </div>
              <div>
                <label className="form-label">District <span className="text-red-500">*</span></label>
                <select className="input-field" value={form.buildingTask5Subtask1Details.districtId} onChange={handleBuildingTask5Subtask1FieldChange('districtId')}>
                  <option value="">Select District</option>
                  {userCellStateId && dropdowns.districts
                    ?.filter((d) => String(d.state_id) === String(userCellStateId))
                    .map((d) => (
                      <option key={d.district_id} value={d.district_id}>
                        {d.district_name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="form-label">Latitude</label>
                <input type="text" className="input-field" value={form.buildingTask5Subtask1Details.latitude} onChange={handleBuildingTask5Subtask1FieldChange('latitude')} placeholder="Enter latitude (optional)" />
              </div>
              <div>
                <label className="form-label">Longitude</label>
                <input type="text" className="input-field" value={form.buildingTask5Subtask1Details.longitude} onChange={handleBuildingTask5Subtask1FieldChange('longitude')} placeholder="Enter longitude (optional)" />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Building Construction Status <span className="text-red-500">*</span></label>
                <select className="input-field" value={form.buildingTask5Subtask1Details.buildingConstructionStatus} onChange={handleBuildingTask5Subtask1FieldChange('buildingConstructionStatus')}>
                  <option value="">Select Construction Status</option>
                  {filteredBuildingStatusesTask5Subtask1.map((s) => (
                    <option key={s.building_status_id} value={s.building_status_id}>{s.building_status}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {showBuildingDetailsTask5Subtask2 && (
        <div className="mt-6">
          <h3 className="text-md font-semibold mb-3">Building Details (Task 5, Subtask 2 - Compliance & EPI)</h3>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <h4 className="font-semibold text-blue-800 mb-2">Building Action</h4>
            {buildingsForCurrentTask.length > 0 ? (
              <p className="text-blue-700 mb-3">You have {buildingsForCurrentTask.length} existing building(s) for this task/subtask combination.</p>
            ) : (
              <p className="text-blue-700 mb-3">No existing buildings found for this task/subtask combination.</p>
            )}
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buildingTask5Subtask2Action"
                  value="update"
                  checked={form.buildingTask5Subtask2Action === 'update'}
                  onChange={(e) => setForm({ ...form, buildingTask5Subtask2Action: e.target.value })}
                  className="mr-2"
                />
                <span>Update an existing building</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buildingTask5Subtask2Action"
                  value="new"
                  checked={form.buildingTask5Subtask2Action === 'new'}
                  onChange={(e) => setForm({ ...form, buildingTask5Subtask2Action: e.target.value })}
                  className="mr-2"
                />
                <span>Add New Building</span>
              </label>
            </div>
          </div>

          {form.buildingTask5Subtask2Action === 'update' && buildingsForCurrentTask.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <h4 className="font-semibold text-blue-800 mb-2">Select Building to Update</h4>
              <select 
                className="input-field mb-3"
                value={form.existingBuildingTask5Subtask2Id || ''}
                onChange={(e) => {
                  const selectedBuilding = buildingsForCurrentTask.find(b => b.building_id == e.target.value);
                  setForm({ 
                    ...form, 
                    existingBuildingTask5Subtask2Id: e.target.value,
                    updateBuildingTask5Subtask2Name: selectedBuilding?.building_name || '',
                    updateBuildingTask5Subtask2Address: selectedBuilding?.building_address || '',
                    updateBuildingTask5Subtask2DistrictId: selectedBuilding?.building_district_id || '',
                    updateBuildingTask5Subtask2ComplianceLevel: selectedBuilding?.compliance_level || '',
                    updateBuildingTask5Subtask2Typology: selectedBuilding?.building_typology || '',
                    updateBuildingTask5Subtask2UlbName: selectedBuilding?.building_nodal_department || '',
                    updateBuildingTask5Subtask2EpiBase: selectedBuilding?.building_epi_value_base || '',
                    updateBuildingTask5Subtask2EpiProposed: selectedBuilding?.building_epi_value_proposed || '',
                    updateBuildingTask5Subtask2Latitude: selectedBuilding?.building_latitude || '',
                    updateBuildingTask5Subtask2Longitude: selectedBuilding?.building_longitude || '',
                    updateBuildingTask5Subtask2StatusId: selectedBuilding?.building_status_id || ''
                  });
                }}
              >
                <option value="">Select Building</option>
                {buildingsForCurrentTask.map((building) => (
                  <option key={building.building_id} value={building.building_id}>
                    {building.building_name} - {building.building_address}
                  </option>
                ))}
              </select>
              
              {form.existingBuildingTask5Subtask2Id && (
                <div className="mt-3">
                  <h5 className="font-semibold text-blue-700 mb-2">Building Details</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Building Name <span className="text-red-500">*</span></label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2Name || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2Name: e.target.value })}
                        placeholder="Enter building name"
                      />
                    </div>
                    <div>
                      <label className="form-label">Building Address <span className="text-red-500">*</span></label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2Address || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2Address: e.target.value })}
                        placeholder="Enter building address"
                      />
                    </div>
                    <div>
                      <label className="form-label">District <span className="text-red-500">*</span></label>
                      <select 
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2DistrictId || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2DistrictId: e.target.value })}
                      >
                        <option value="">Select District</option>
                        {userCellStateId && dropdowns.districts
                          ?.filter((d) => String(d.state_id) === String(userCellStateId))
                          .map((d) => (
                            <option key={d.district_id} value={d.district_id}>
                              {d.district_name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Latitude</label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2Latitude || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2Latitude: e.target.value })}
                        placeholder="Enter latitude (optional)"
                      />
                    </div>
                    <div>
                      <label className="form-label">Longitude</label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2Longitude || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2Longitude: e.target.value })}
                        placeholder="Enter longitude (optional)"
                      />
                    </div>
                    <div>
                      <label className="form-label">Compliance Level <span className="text-red-500">*</span></label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2ComplianceLevel || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2ComplianceLevel: e.target.value })}
                        placeholder="Enter compliance level"
                      />
                    </div>
                    <div>
                      <label className="form-label">Typology <span className="text-red-500">*</span></label>
                      <select
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2Typology || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2Typology: e.target.value })}
                      >
                        <option value="">Select Typology</option>
                        {filteredTypologies && filteredTypologies.map((typology) => (
                          <option key={typology.typologyid || typology.TypologyId} value={typology.typologyname || typology.TypologyName}>
                            {typology.typologyname || typology.TypologyName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Name of ULB <span className="text-red-500">*</span></label>
                      <input 
                        type="text"
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2UlbName || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2UlbName: e.target.value })}
                        placeholder="Enter ULB name"
                      />
                    </div>
                    <div>
                      <label className="form-label">EPI Base</label>
                      <input 
                        type="number"
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2EpiBase || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2EpiBase: e.target.value })}
                        placeholder="EPI Base (optional)"
                      />
                    </div>
                    <div>
                      <label className="form-label">EPI Proposed</label>
                      <input 
                        type="number"
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2EpiProposed || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2EpiProposed: e.target.value })}
                        placeholder="EPI Proposed (optional)"
                      />
                    </div>
                    <div>
                      <label className="form-label">EPI Ratio (Auto-calculated)</label>
                      <input 
                        type="text"
                        className="input-field bg-gray-100"
                        value={form.updateBuildingTask5Subtask2EpiBase && form.updateBuildingTask5Subtask2EpiProposed ? (parseFloat(form.updateBuildingTask5Subtask2EpiProposed) / parseFloat(form.updateBuildingTask5Subtask2EpiBase)).toFixed(4) : ''}
                        readOnly
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="form-label">Building Status</label>
                      <select 
                        className="input-field"
                        value={form.updateBuildingTask5Subtask2StatusId || ''}
                        onChange={(e) => setForm({ ...form, updateBuildingTask5Subtask2StatusId: e.target.value })}
                      >
                        <option value="">Select Status</option>
                        {dropdowns.buildingStatuses
                          ?.filter((s) => s.isother_all === true)
                          .map((s) => (
                            <option key={s.building_status_id} value={s.building_status_id}>
                              {s.building_status}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {form.buildingTask5Subtask2Action === 'new' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Building Name <span className="text-red-500">*</span></label>
                <input 
                  className="input-field" 
                  value={form.buildingTask5Subtask2Details.name} 
                  onChange={handleBuildingTask5Subtask2FieldChange('name')} 
                  placeholder="Enter building name" 
                />
              </div>
              <div>
                <label className="form-label">Building Address <span className="text-red-500">*</span></label>
                <input 
                  className="input-field" 
                  value={form.buildingTask5Subtask2Details.address} 
                  onChange={handleBuildingTask5Subtask2FieldChange('address')} 
                  placeholder="Enter building address" 
                />
              </div>
              <div>
                <label className="form-label">District <span className="text-red-500">*</span></label>
                <select 
                  className="input-field" 
                  value={form.buildingTask5Subtask2Details.districtId} 
                  onChange={handleBuildingTask5Subtask2FieldChange('districtId')}
                >
                  <option value="">Select District</option>
                  {userCellStateId && dropdowns.districts
                    ?.filter((d) => String(d.state_id) === String(userCellStateId))
                    .map((d) => (
                      <option key={d.district_id} value={d.district_id}>
                        {d.district_name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="form-label">Latitude</label>
                <input 
                  type="text"
                  className="input-field" 
                  value={form.buildingTask5Subtask2Details.latitude} 
                  onChange={handleBuildingTask5Subtask2FieldChange('latitude')} 
                  placeholder="Enter latitude (optional)" 
                />
              </div>
              <div>
                <label className="form-label">Longitude</label>
                <input 
                  type="text"
                  className="input-field" 
                  value={form.buildingTask5Subtask2Details.longitude} 
                  onChange={handleBuildingTask5Subtask2FieldChange('longitude')} 
                  placeholder="Enter longitude (optional)" 
                />
              </div>
              <div>
                <label className="form-label">Compliance Level <span className="text-red-500">*</span></label>
                <input 
                  className="input-field" 
                  value={form.buildingTask5Subtask2Details.complianceLevel} 
                  onChange={handleBuildingTask5Subtask2FieldChange('complianceLevel')} 
                  placeholder="Enter compliance level" 
                />
              </div>
              <div>
                <label className="form-label">Typology <span className="text-red-500">*</span></label>
                <select
                  className="input-field"
                  value={form.buildingTask5Subtask2Details.typology}
                  onChange={handleBuildingTask5Subtask2FieldChange('typology')}
                >
                  <option value="">Select Typology</option>
                  {filteredTypologies && filteredTypologies.map((typology) => (
                    <option key={typology.typologyid || typology.TypologyId} value={typology.typologyname || typology.TypologyName}>
                      {typology.typologyname || typology.TypologyName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Name of ULB <span className="text-red-500">*</span></label>
                <input 
                  className="input-field" 
                  value={form.buildingTask5Subtask2Details.ulbName} 
                  onChange={handleBuildingTask5Subtask2FieldChange('ulbName')} 
                  placeholder="Enter ULB name" 
                />
              </div>
              <div>
                <label className="form-label">EPI Base</label>
                <input 
                  type="number"
                  className="input-field" 
                  value={form.buildingTask5Subtask2Details.epiBase} 
                  onChange={handleBuildingTask5Subtask2FieldChange('epiBase')} 
                  placeholder="EPI Base (optional)" 
                />
              </div>
              <div>
                <label className="form-label">EPI Proposed</label>
                <input 
                  type="number"
                  className="input-field" 
                  value={form.buildingTask5Subtask2Details.epiProposed} 
                  onChange={handleBuildingTask5Subtask2FieldChange('epiProposed')} 
                  placeholder="EPI Proposed (optional)" 
                />
              </div>
              <div>
                <label className="form-label">EPI Ratio (Auto-calculated)</label>
                <input 
                  type="text"
                  className="input-field bg-gray-100" 
                  value={form.buildingTask5Subtask2Details.epiBase && form.buildingTask5Subtask2Details.epiProposed ? (parseFloat(form.buildingTask5Subtask2Details.epiProposed) / parseFloat(form.buildingTask5Subtask2Details.epiBase)).toFixed(4) : ''}
                  readOnly
                />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Building Status</label>
                <select 
                  className="input-field"
                  value={form.buildingTask5Subtask2Details.statusId}
                  onChange={handleBuildingTask5Subtask2FieldChange('statusId')}
                >
                  <option value="">Select Status</option>
                  {dropdowns.buildingStatuses
                    ?.filter((s) => s.isother_all === true)
                    .map((s) => (
                      <option key={s.building_status_id} value={s.building_status_id}>
                        {s.building_status}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status, Document, and Remarks fields - shown after subtask-specific forms */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="flex flex-col">
          <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-2">
            <CheckCircle2 className="h-4 w-4 text-blue-600" />
            Status <span className="text-red-500">*</span>
          </label>
          <div className="flex-1">
            <select 
              className="w-full input-field border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg transition-all h-[42px]" 
              value={form.statusId} 
              onChange={(e) => setForm({ ...form, statusId: e.target.value })}
            >
              <option value="">Select Status</option>
              {dropdowns.statuses.map((s) => (
                <option key={s.status_id} value={s.status_id}>{s.status}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Stakeholder Name Field - Show only when "Awaiting Stakeholder's response" is selected */}
        {(() => {
          const selectedStatus = dropdowns.statuses.find(s => String(s.status_id) === String(form.statusId));
          const isAwaitingStakeholder = selectedStatus && selectedStatus.status && 
            selectedStatus.status.toLowerCase().trim().includes('awaiting') && 
            selectedStatus.status.toLowerCase().trim().includes('stakeholder');
          
          if (isAwaitingStakeholder) {
            return (
              <div className="flex flex-col mb-6">
                <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-2">
                  <Users className="h-4 w-4 text-blue-600" />
                  Stakeholder's Name <span className="text-red-500">*</span>
                </label>
                <div className="flex-1">
                  <input
                    type="text"
                    className="w-full input-field border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg transition-all h-[42px]"
                    placeholder="Enter Stakeholder's Name"
                    value={form.stakeholderName || ''}
                    onChange={(e) => setForm({ ...form, stakeholderName: e.target.value })}
                  />
                </div>
              </div>
            );
          }
          return null;
        })()}
        
        <div className="flex flex-col">
          <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-2">
            <Upload className="h-4 w-4 text-blue-600" />
            Document {(() => {
              const selectedStatus = dropdowns.statuses.find(s => String(s.status_id) === String(form.statusId));
              const isCompleted = selectedStatus && selectedStatus.status && selectedStatus.status.toLowerCase().trim() === 'completed';
              return isCompleted ? <span className="text-red-500">*</span> : null;
            })()}
          </label>
          <div className="flex-1 relative">
            <div className="flex items-center gap-2">
              <label className="flex-1 cursor-pointer">
                <input 
                  type="file" 
                  onChange={onFileChange} 
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
                />
                <div className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-lg bg-white hover:border-blue-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all duration-200 flex items-center justify-between h-[42px]">
                  <span className={`text-sm ${form.documents.length > 0 || form.documentPaths.length > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                    {(() => {
                      const totalFiles = form.documents.length + form.documentPaths.length;
                      if (totalFiles === 0) return 'No file chosen';
                      if (totalFiles === 1) return '1 file selected';
                      return `${totalFiles} files selected`;
                    })()}
                  </span>
                  <span className="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                    Choose file
                  </span>
                </div>
              </label>
            </div>
              <p className="mt-1 text-xs text-gray-500">
              Max file size: 5 MB per file. Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG, ZIP
              </p>
            
            {/* Display new uploaded files */}
            {form.documents.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-sm font-semibold text-gray-700">New Files:</p>
                {form.documents.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocument(index)}
                      className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                      title="Remove file"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Display existing document paths (for editing) */}
            {form.documentPaths.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-sm font-semibold text-gray-700">Existing Files:</p>
                {form.documentPaths.map((path, index) => {
                  const pathParts = path.split('/');
                  const fileName = pathParts[pathParts.length - 1] || path;
                  return (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{fileName}</p>
                <a 
                          href={`${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`}
                  target="_blank"
                  rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                >
                  View Document
                </a>
              </div>
                      <button
                        type="button"
                        onClick={() => removeExistingDocument(index)}
                        className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                        title="Remove file"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="md:col-span-3 flex flex-col">
          <label className="form-label flex items-center gap-2 text-gray-700 font-semibold mb-2">
            <MessageSquare className="h-4 w-4 text-blue-600" />
            Remarks <span className="text-red-500">*</span>
          </label>
          <div className="flex-1">
            <style>{`
              .quill-container .ql-container {
                min-height: 150px;
                border-radius: 0 0 0.5rem 0.5rem;
                border: 2px solid #e5e7eb;
                border-top: none;
              }
              .quill-container .ql-toolbar {
                border: 2px solid #e5e7eb;
                border-radius: 0.5rem 0.5rem 0 0;
                background: #f9fafb;
              }
              .quill-container .ql-container:focus-within {
                border-color: #3b82f6;
                box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
              }
              .quill-container .ql-toolbar:focus-within {
                border-color: #3b82f6;
              }
              .quill-container .ql-editor {
                min-height: 150px;
              }
              .quill-container .ql-editor.ql-blank::before {
                color: #9ca3af;
                font-style: normal;
              }
            `}</style>
            <div className="quill-container">
              <ReactQuill
                theme="snow"
                value={form.remarks || ''}
                onChange={(value) => setForm({ ...form, remarks: value })}
              placeholder="Enter any additional remarks or notes..."
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'align': [] }],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['clean']
                  ]
                }}
                formats={[
                  'header',
                  'bold', 'italic', 'underline', 'strike',
                  'color', 'background',
                  'align',
                  'list', 'bullet'
                ]}
            />
          </div>
        </div>
      </div>
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap gap-3">
        <button 
          className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-all shadow-md hover:shadow-lg"
          onClick={resetForm}
        >
          <RotateCcw className="h-5 w-5" />
          Reset
        </button>
        <button 
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg ${
            loading 
              ? 'bg-blue-400 text-white cursor-not-allowed opacity-75' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
          onClick={() => saveTask(false)}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Save
            </>
          )}
        </button>
      </div>
    </div>
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Task Tracker</h1>
      </div>

      {/* Week Numbering Info Banner */}

      <div className="flex gap-3 mb-6 p-1 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl shadow-inner border border-gray-200">
        <button 
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 transform ${
            activeTab === 'add' 
              ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/50 scale-105' 
              : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md hover:scale-102'
          }`} 
          onClick={() => {
            setActiveTab('add');
            if (editingTaskId) {
              resetForm();
            }
          }}
        >
          <Plus className={`h-5 w-5 ${activeTab === 'add' ? 'text-white' : 'text-purple-600'}`} />
          <span>Add New Task</span>
        </button>
        <button 
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 transform ${
            activeTab === 'saved' 
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/50 scale-105' 
              : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md hover:scale-102'
          }`} 
          onClick={() => setActiveTab('saved')}
        >
          <FileText className={`h-5 w-5 ${activeTab === 'saved' ? 'text-white' : 'text-blue-600'}`} />
          <span>Saved Tasks</span>
        </button>
        <button 
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 transform ${
            activeTab === 'submitted' 
              ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/50 scale-105' 
              : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md hover:scale-102'
          }`} 
          onClick={() => setActiveTab('submitted')}
        >
          <CheckCircle2 className={`h-5 w-5 ${activeTab === 'submitted' ? 'text-white' : 'text-green-600'}`} />
          <span>Submitted Tasks</span>
        </button>
        <button 
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 transform ${
            activeTab === 'payment' 
              ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/50 scale-105' 
              : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md hover:scale-102'
          }`} 
          onClick={() => setActiveTab('payment')}
        >
          <CreditCard className={`h-5 w-5 ${activeTab === 'payment' ? 'text-white' : 'text-emerald-600'}`} />
          <span>Payment Status</span>
        </button>
      </div>

      {activeTab === 'submitted' && (
        <div className="card shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              Submitted Tasks
            </h3>
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
              {submittedTasks.length} {submittedTasks.length === 1 ? 'Task' : 'Tasks'}
            </span>
          </div>
          <div className="overflow-x-auto overflow-y-visible">
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white" style={{ minWidth: '1400px' }}>
              <table className="w-full text-sm" style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-300">
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left" style={{ width: '180px' }}>
                      <Calendar className="h-4 w-4 text-gray-500 inline mr-2" />
                      Week
                    </th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-120">Quarter</th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-120">
                      <Target className="h-4 w-4 text-gray-500 inline mr-2" />
                      Task
                    </th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-350">Subtask</th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-250">
                      <FileText className="h-4 w-4 text-gray-500 inline mr-2" />
                      Remarks
                    </th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-150">Status</th>
                    <th className="py-4 px-5 font-bold text-gray-700 text-left table-col-150">Attachment</th>
                  </tr>
                </thead>
                <tbody>
                  {submittedTasks.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-400 border-r border-gray-200">
                        <div className="flex flex-col items-center gap-2">
                          <FileText className="h-12 w-12 text-gray-300" />
                          <p className="text-lg font-medium">No submitted tasks found</p>
                          <p className="text-sm">Tasks you submit will appear here</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    submittedTasks.map((t, idx) => (
                      <tr 
                        key={t.id} 
                        className={`border-b border-gray-200 transition-all duration-150 hover:bg-blue-50 hover:shadow-sm ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        <td className="py-4 px-5 border-r border-gray-200" style={{ width: '180px' }}>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-700 text-xs">{getWeekFromDate(t.task_date)}</span>
                          </div>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-120">
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-semibold whitespace-nowrap">
                            {t.quarter_name}
                          </span>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-120">
                          <div className="flex items-center gap-2">
                            <div 
                              className="task-indicator" 
                              style={{ '--task-color': getTaskColor(t.task_no) }}
                            ></div>
                            <span className="whitespace-nowrap text-task-color" style={{ '--task-color': getTaskColor(t.task_no) }}>
                              Task {t.task_no}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-350">
                          <div className="break-words overflow-hidden">
                            <span className="font-medium text-gray-800">Subtask {t.subtask_no}:</span>
                            <span className="text-gray-600 ml-1">{t.subtask_description || '-'}</span>
                          </div>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-250">
                          <div className="break-words overflow-hidden">
                            {t.remarks ? (
                              <div className="text-gray-600 prose prose-sm max-w-none word-break-safe">
                                <RemarksWithStakeholder remarks={t.remarks} />
                              </div>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-150">
                          <div className="flex items-center">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold break-words max-w-full ${getStatusColor(t.status)}`}>
                              {t.status === 'Completed' && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />}
                              {t.status === 'In Progress' && <Clock className="h-3.5 w-3.5 flex-shrink-0" />}
                              {t.status === 'On Hold' && <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />}
                              {t.status === 'Not Started' && <XCircle className="h-3.5 w-3.5 flex-shrink-0" />}
                              <span className="break-words">{t.status}</span>
                          </span>
                          </div>
                        </td>
                        <td className="py-4 px-5 table-col-150">
                          {(() => {
                            if (!t.document_path) return <span className="text-gray-300">-</span>;
                            
                            // Handle different formats of document_path
                            let documentPaths = [];
                            
                            // If it's already an array (shouldn't happen but just in case)
                            if (Array.isArray(t.document_path)) {
                              documentPaths = t.document_path;
                            } else if (typeof t.document_path === 'string') {
                              // Try to parse as JSON
                              try {
                                const parsed = JSON.parse(t.document_path);
                                documentPaths = Array.isArray(parsed) ? parsed : [t.document_path];
                              } catch (e) {
                                // If JSON.parse fails, check if it looks like a JSON array string
                                const trimmed = t.document_path.trim();
                                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                                  // Try manual parsing
                                  try {
                                    const manualParse = trimmed.slice(1, -1).split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
                                    documentPaths = manualParse.filter(p => p.length > 0);
                                  } catch (e2) {
                                    // If all parsing fails, treat as single path
                                    documentPaths = [t.document_path];
                                  }
                                } else {
                                  // Not a JSON array, treat as single path
                                  documentPaths = [t.document_path];
                                }
                              }
                            } else {
                              documentPaths = [String(t.document_path)];
                            }
                            
                            if (documentPaths.length === 0) {
                              return <span className="text-gray-300">-</span>;
                            } else if (documentPaths.length === 1) {
                              const docPath = documentPaths[0].startsWith('/') ? documentPaths[0] : `/${documentPaths[0]}`;
                              return (
                            <a
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
                                  href={`${window.location.origin}${docPath}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Download className="h-4 w-4" />
                              View
                            </a>
                              );
                            } else {
                              return (
                                <div className="flex flex-col gap-1">
                                  {documentPaths.map((path, idx) => {
                                    const pathParts = path.split('/');
                                    const fileName = pathParts[pathParts.length - 1] || `Document ${idx + 1}`;
                                    const docPath = path.startsWith('/') ? path : `/${path}`;
                                    return (
                                      <a
                                        key={idx}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                                        href={`${window.location.origin}${docPath}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={fileName}
                                      >
                                        <Download className="h-3 w-3" />
                                        View
                                      </a>
                                    );
                                  })}
                                </div>
                              );
                            }
                          })()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'payment' && (
        <div className="card shadow-lg border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md">
                <CreditCard className="h-5 w-5" />
              </span>
              Payment Status
            </h3>
            <span className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-semibold border border-emerald-200/60 shadow-sm">
              {paymentStatusEntries.length} {paymentStatusEntries.length === 1 ? 'Entry' : 'Entries'}
            </span>
          </div>

          <form onSubmit={handlePaymentFormSubmit} className="mb-8">
            <div className="p-5 rounded-xl bg-gradient-to-br from-gray-50/80 to-emerald-50/30 border border-gray-100 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Quarter <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={paymentForm.quarterId}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, quarterId: e.target.value }))}
                    className={`w-full h-11 px-4 rounded-lg border-2 bg-white shadow-sm transition-all focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none ${
                      paymentFormErrors.quarterId ? 'border-red-400' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                  <option value="">Select Quarter</option>
                  {(dropdowns.quarters || []).map((q) => {
                    const isReleased = releasedQuarterIds.has(String(q.id));
                    return (
                      <option key={q.id} value={q.id} disabled={isReleased}>
                        {q.quarter_name ?? q.Quarter_Name ?? `Q${q.quarter_no ?? q.Quarter_No}`}
                        {isReleased ? ' (Released)' : ''}
                      </option>
                    );
                  })}
                </select>
                {paymentFormErrors.quarterId && (
                  <p className="mt-1 text-sm text-red-500">{paymentFormErrors.quarterId}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Payment Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={paymentForm.paymentStatusId}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, paymentStatusId: e.target.value }))}
                  className={`w-full h-11 px-4 rounded-lg border-2 bg-white shadow-sm transition-all focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none ${
                    paymentFormErrors.paymentStatusId ? 'border-red-400' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <option value="">Select Payment Status</option>
                  {paymentStatusOptions.map((opt) => {
                    const optId = Number(opt.id);
                    const isDisabled = optId < currentMaxPaymentStatusIdForSelectedQuarter;
                    return (
                      <option key={opt.id} value={opt.id} disabled={isDisabled}>
                        {opt.status ?? opt.payment_status ?? opt.name ?? `Status ${opt.id}`}
                      </option>
                    );
                  })}
                </select>
                {paymentFormErrors.paymentStatusId && (
                  <p className="mt-1 text-sm text-red-500">{paymentFormErrors.paymentStatusId}</p>
                )}
              </div>
              {paymentStatusRequiresDate && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Payment Received Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={paymentForm.paymentReceivedDate}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, paymentReceivedDate: e.target.value }))}
                    className={`w-full h-11 px-4 rounded-lg border-2 bg-white shadow-sm transition-all focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none ${
                      paymentFormErrors.paymentReceivedDate ? 'border-red-400' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  />
                  {paymentFormErrors.paymentReceivedDate && (
                    <p className="mt-1 text-sm text-red-500">{paymentFormErrors.paymentReceivedDate}</p>
                  )}
                </div>
              )}
              <div className={paymentStatusRequiresDate ? '' : 'md:col-span-2'}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks</label>
                <input
                  type="text"
                  value={paymentForm.remarks}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, remarks: e.target.value }))}
                  placeholder="Add remarks if any"
                  className="w-full h-11 px-4 rounded-lg border-2 border-gray-200 bg-white shadow-sm transition-all focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none hover:border-gray-300"
                />
              </div>
            </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-md hover:shadow-lg hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98]"
              >
                <Save className="h-5 w-5" />
                {loading ? (editingPaymentId ? 'Updating...' : 'Saving...') : (editingPaymentId ? 'Update Payment Status' : 'Save Payment Status')}
              </button>
            </div>
          </form>

          <div className="pt-6">
            <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-emerald-500" />
              Saved payment status
            </h4>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-md">
              <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <th className="py-3.5 px-4 font-bold text-gray-700 text-left border border-gray-300">Quarter</th>
                    <th className="py-3.5 px-4 font-bold text-gray-700 text-left border border-gray-300">Payment Status</th>
                    <th className="py-3.5 px-4 font-bold text-gray-700 text-left border border-gray-300">Remarks</th>
                    <th className="py-3.5 px-4 font-bold text-gray-700 text-left border border-gray-300">Payment Received Date</th>
                    <th className="py-3.5 px-4 font-bold text-gray-700 text-left border border-gray-300">Submission Date</th>
                    <th className="py-3.5 px-4 font-bold text-gray-700 text-left border border-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentStatusEntries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-10 text-center text-gray-400 border border-gray-300 bg-gray-50/50">
                        No payment status entries yet. Use the form above to add one.
                      </td>
                    </tr>
                  ) : (
                    paymentStatusEntries.map((entry, idx) => (
                      <tr key={entry.id} className={idx % 2 === 0 ? 'bg-white hover:bg-gray-50/70' : 'bg-gray-50/50 hover:bg-gray-100/50'}>
                        <td className="py-3 px-4 border border-gray-300">
                          <span className="font-medium text-gray-800">{entry.quarter_name ?? `Q${entry.quarter_no}`}</span>
                        </td>
                        <td className="py-3 px-4 border border-gray-300">
                          {(paymentStatusOptions.find((o) => String(o.id) === String(entry.payment_status_id))?.status ?? paymentStatusOptions.find((o) => String(o.id) === String(entry.payment_status_id))?.payment_status ?? paymentStatusOptions.find((o) => String(o.id) === String(entry.payment_status_id))?.name) || entry.payment_status_id}
                        </td>
                        <td className="py-3 px-4 text-gray-600 border border-gray-300">{entry.remarks || '–'}</td>
                        <td className="py-3 px-4 text-gray-600 border border-gray-300">
                          {entry.payment_received_date ? new Date(entry.payment_received_date).toLocaleDateString() : '–'}
                        </td>
                        <td className="py-3 px-4 text-gray-600 border border-gray-300">
                          {entry.submission_date ? new Date(entry.submission_date).toLocaleDateString() : '–'}
                        </td>
                        <td className="py-3 px-4 border border-gray-300">
                          {entry.is_submitted ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200/60 shadow-sm">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Submitted to BEE
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => handlePaymentSubmitEntry(entry)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-colors"
                              >
                                <Send className="h-4 w-4" /> Submit to BEE
                              </button>
                              <button
                                type="button"
                                onClick={() => handlePaymentEdit(entry)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gray-600 hover:bg-gray-700 shadow-sm transition-colors"
                              >
                                <Edit className="h-4 w-4" /> Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handlePaymentDelete(entry)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 shadow-sm transition-colors"
                              >
                                <Trash2 className="h-4 w-4" /> Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'saved' && (
        <div className="card shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-6 w-6 text-blue-600" />
              Saved Tasks
            </h3>
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
              {savedTasks.length} {savedTasks.length === 1 ? 'Task' : 'Tasks'}
            </span>
          </div>
          <div className="overflow-x-auto overflow-y-visible">
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white" style={{ minWidth: '1600px' }}>
              <table className="w-full text-sm" style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-300">
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left" style={{ width: '180px' }}>
                      <Calendar className="h-4 w-4 text-gray-500 inline mr-2" />
                      Week
                    </th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-120">Quarter</th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-120">
                      <Target className="h-4 w-4 text-gray-500 inline mr-2" />
                      Task
                    </th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-350">Subtask</th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-250">
                      <FileText className="h-4 w-4 text-gray-500 inline mr-2" />
                      Remarks
                    </th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-150">Status</th>
                    <th className="py-4 px-5 font-bold text-gray-700 border-r border-gray-200 text-left table-col-150">Attachment</th>
                    <th className="py-4 px-5 font-bold text-gray-700 text-left col-width-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {savedTasks.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-gray-400 border-r border-gray-200">
                        <div className="flex flex-col items-center gap-2">
                          <FileText className="h-12 w-12 text-gray-300" />
                          <p className="text-lg font-medium">No saved tasks found</p>
                          <p className="text-sm">Tasks you save will appear here</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    savedTasks.map((t, idx) => (
                      <tr 
                        key={t.id} 
                        className={`border-b border-gray-200 transition-all duration-150 hover:bg-blue-50 hover:shadow-sm ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        <td className="py-4 px-5 border-r border-gray-200" style={{ width: '180px' }}>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-700 text-xs">{getWeekFromDate(t.task_date)}</span>
                          </div>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-120">
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-semibold whitespace-nowrap">
                            {t.quarter_name}
                          </span>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-120">
                          <div className="flex items-center gap-2">
                            <div 
                              className="task-indicator" 
                              style={{ '--task-color': getTaskColor(t.task_no) }}
                            ></div>
                            <span className="whitespace-nowrap text-task-color" style={{ '--task-color': getTaskColor(t.task_no) }}>
                              Task {t.task_no}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-350">
                          <div className="break-words overflow-hidden">
                            <span className="font-medium text-gray-800">Subtask {t.subtask_no}:</span>
                            <span className="text-gray-600 ml-1">{t.subtask_description || '-'}</span>
                          </div>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-250">
                          <div className="break-words overflow-hidden">
                            {t.remarks ? (
                              <div className="text-gray-600 prose prose-sm max-w-none word-break-safe">
                                <RemarksWithStakeholder remarks={t.remarks} />
                              </div>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-150">
                          <div className="flex items-center">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold break-words max-w-full ${getStatusColor(t.status)}`}>
                              {t.status === 'Completed' && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />}
                              {t.status === 'In Progress' && <Clock className="h-3.5 w-3.5 flex-shrink-0" />}
                              {t.status === 'On Hold' && <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />}
                              {t.status === 'Not Started' && <XCircle className="h-3.5 w-3.5 flex-shrink-0" />}
                              <span className="break-words">{t.status}</span>
                          </span>
                          </div>
                        </td>
                        <td className="py-4 px-5 border-r border-gray-200 table-col-150">
                          {(() => {
                            if (!t.document_path) return <span className="text-gray-300">-</span>;
                            
                            // Handle different formats of document_path
                            let documentPaths = [];
                            
                            // If it's already an array (shouldn't happen but just in case)
                            if (Array.isArray(t.document_path)) {
                              documentPaths = t.document_path;
                            } else if (typeof t.document_path === 'string') {
                              // Try to parse as JSON
                              try {
                                const parsed = JSON.parse(t.document_path);
                                documentPaths = Array.isArray(parsed) ? parsed : [t.document_path];
                              } catch (e) {
                                // If JSON.parse fails, check if it looks like a JSON array string
                                const trimmed = t.document_path.trim();
                                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                                  // Try manual parsing
                                  try {
                                    const manualParse = trimmed.slice(1, -1).split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
                                    documentPaths = manualParse.filter(p => p.length > 0);
                                  } catch (e2) {
                                    // If all parsing fails, treat as single path
                                    documentPaths = [t.document_path];
                                  }
                                } else {
                                  // Not a JSON array, treat as single path
                                  documentPaths = [t.document_path];
                                }
                              }
                            } else {
                              documentPaths = [String(t.document_path)];
                            }
                            
                            if (documentPaths.length === 0) {
                              return <span className="text-gray-300">-</span>;
                            } else if (documentPaths.length === 1) {
                              return (
                            <a
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
                                  href={`${window.location.origin}${documentPaths[0].startsWith('/') ? documentPaths[0] : `/${documentPaths[0]}`}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Download className="h-4 w-4" />
                              View
                            </a>
                              );
                            } else {
                              return (
                                <div className="flex flex-col gap-1">
                                  {documentPaths.map((path, idx) => {
                                    const pathParts = path.split('/');
                                    const fileName = pathParts[pathParts.length - 1] || `Document ${idx + 1}`;
                                    return (
                                      <a
                                        key={idx}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                                        href={`${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={fileName}
                                      >
                                        <Download className="h-3 w-3" />
                                        View
                                      </a>
                                    );
                                  })}
                                </div>
                              );
                            }
                          })()}
                        </td>
                        <td className="py-4 px-5 width-300">
                          <div className="flex gap-2 flex-wrap">
                            <button 
                              className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors shadow-sm hover:shadow-md"
                              onClick={() => handleSubmitTask(t.id)}
                            >
                              <Send className="h-4 w-4" />
                              Submit
                            </button>
                            <button 
                              className="inline-flex items-center gap-1 px-3 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors shadow-sm hover:shadow-md"
                              onClick={() => handleEdit(t)}
                            >
                              <Edit className="h-4 w-4" />
                              Edit
                            </button>
                            <button 
                              className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors shadow-sm hover:shadow-md"
                              onClick={() => handleDeleteTask(t.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'add' && FormSection}
    </div>
  );
};

export default TaskTracker;



